import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme } from 'antd';
import ChatWidget from '@mia-platform-internal/chatbot-widget';
import '@mia-platform-internal/chatbot-widget/style.css';

import config from '../config';
import { buildAclContextHeaders } from '../utils/aclContext';
import { toolPayload, isToolPayloadSuccess } from '../utils/toolPayload';
import { useAuth } from '../context/AuthContext';
import CommandResultModal from './CommandResultModal';
import WorkflowTimeline, { WorkflowTimelineToggle, toolCallStatus } from './WorkflowTimeline';
import PlaybookPicker from './PlaybookPicker';
import PlanRefusalNotice from './sandbox/PlanRefusalNotice';
import CreditsNotice, { useChatCredits } from './sandbox/CreditsNotice';
import RateLimitNotice from './RateLimitNotice';
import { rateLimitFromTurnError } from '../utils/llmRateLimit';
import OwnKeyPrompt from './sandbox/OwnKeyPrompt';
import useOwnModelKey from '../hooks/useOwnModelKey';
import { NEEDS_AI_FOUNDRY } from '../utils/aiFoundryGate';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import { listRuns, startRun, cancelRun } from '../services/WorkflowsService';
import { cancelAdkSession } from '../services/AdkSessionsService';
import { deferChatDocument, storeChatAttachment } from '../utils/chatAttachmentStore';
import { listPlaybooks, selectPlaybookServerSide } from '../services/PlaybooksService';
import usePlanFeatures, { planLabel } from '../hooks/usePlanFeatures';
import { listDirectoryEntries, readFile, registerVfsService, findProjectByConversationId, getVfsServiceNames, markProjectVfsBacked, readProjectMetadata, updateProjectMetadata } from '../utils/vfsClient';
import { forceResyncServiceFromVfs } from '../utils/forceResyncServiceFromVfs';
import { getRandomSuggestions } from '../utils/suggestionPrompts';
import { addPendingVfsResync } from '../utils/pendingVfsResync';
import './VfsChatPanel.css';
import { offerVoiceHandoff, onVoiceHandoff, takeVoiceHandoff } from '../utils/voiceHandoff';
import { prefillChatInput } from '../utils/askAiInChat';
import { previewResult, turnCompleted, undone } from '../analytics/journey';

// Flow's default, general-purpose playbook (adk-be-app's flow-assistant-
// playbook/-agent) — supersedes code-generation-playbook as the fallback
// used whenever nothing more specific has been picked (a brand-new chat,
// or "Auto" resolving with nothing decisive to go on). Same vfs/preview/
// brain toolset, broader framing — see that agent's own instruction.
const DEFAULT_PLAYBOOK_NAME = 'flow-assistant-playbook';
// The only assistant while the person's AI Foundry connection is off (usePlanFeatures): Flow's default playbook,
// built into adk-be-app (its seed data), so no Catalog playbook list is needed to talk to it.
const DEFAULT_PLAYBOOK_OPTION = {
    name: DEFAULT_PLAYBOOK_NAME,
    displayName: 'Flow Assistant',
    description: 'Flow’s default assistant',
};

// Same nonsense-verb list as the vendored chat widget's own WaitingIndicator
// (mia-chatbot-widget's ChatMessage.tsx, `chatMessage.waiting.0..29` in its
// en.ts) — duplicated here rather than exported from the widget's public API
// because this overlay (the "sending your message" screen, entirely inside
// mia-flow-fe, before the widget itself has even mounted with real content)
// isn't part of the widget's own component tree at all, so there's nothing
// to import from. Keeping the same words/cadence is what makes this read as
// "the same waiting UI", not a new invented one.
const SENDING_WAITING_WORDS = [
    'Fluencifying…', 'Alveating…', 'Currenciting…', 'Fluxiographing…', 'Modifluxing…',
    'Fabristreaming…', 'Continuating…', 'Codexifying…', 'Nexusating…', 'Substrating…',
    'Stratifying…', 'Stactuating…', 'Matrixifying…', 'Architenting…', 'Operatizing…',
    'Rubricating…', 'Vectorating…', 'Nodusating…', 'Syntagming…', 'Tractating…',
    'Tactitoring…', 'Schemaflowing…', 'Iteratizing…', 'Cognimating…', 'Texificating…',
    'Volitiating…', 'Synapsating…', 'Procecting…', 'Nodiweaving…', 'Intellectating…',
];
const SENDING_WAITING_WORD_INTERVAL_MS = 2200;
// The longest a pendingMessage handed in at mount waits for the widget to open its conversation (see widgetSettled).
const PENDING_MESSAGE_SETTLE_MS = 3000;
// How often, and for how long at most, a pendingDraft looks for the widget's input to write into.
const PREFILL_RETRY_MS = 150;
const PREFILL_GIVE_UP_MS = 6000;

// Same hexagon shape + color family as Home.jsx's ambient background, but
// generated and positioned independently rather than reusing Home's own
// home-hexagon-N classes: two rounds of that literal reuse (opacity bump,
// then repositioning) were still live-reported as invisible — Home.css's
// rules are tuned/animated for its own full-page hero and fighting that
// cascade for a small embedded panel kept losing in ways that were hard to
// pin down further. This generates its own random hexagons (count/size/
// position/drift all randomized per mount) with its own much higher,
// non-negotiable opacity, so visibility no longer depends on out-guessing
// another component's CSS.
const SENDING_HEXAGON_COLORS = [
    'rgba(124, 58, 237, 0.95)', 'rgba(0, 180, 216, 0.95)', 'rgba(167, 139, 250, 0.95)',
    'rgba(79, 209, 197, 0.95)', 'rgba(196, 181, 253, 0.95)', 'rgba(34, 211, 238, 0.95)',
    'rgba(139, 92, 246, 0.95)', 'rgba(103, 232, 249, 0.95)',
];
const makeSendingHexagons = () => Array.from({ length: 10 }, (_, i) => {
    return {
        id: i,
        // Which of the 10 fixed drift keyframes (VfsChatPanel.css,
        // .vfs-chat-sending-hex-N) this hexagon uses — the keyframe itself
        // bakes in the translate distance/direction, only position/size/
        // timing are randomized per mount.
        driftIndex: i + 1,
        top: `${Math.round(Math.random() * 85)}%`,
        left: `${Math.round(Math.random() * 85)}%`,
        size: Math.round(50 + Math.random() * 170),
        // BUG FIX (live-reported: "hexagon dont move, why?"): this overlay
        // is only ever up for ~2s (a quick classification call, not a full
        // page visit) — the original 16-32s duration was copied from
        // Home's own hero-scale float speed, which is fine for a
        // full-page background someone looks at for a while, but on this
        // timescale less than a tenth of one cycle ever plays, reading as
        // fully static. Sped up an order of magnitude so real motion is
        // visible within the actual lifetime of this overlay; negative
        // delay range shrunk to match, so no hexagon spends the whole
        // window sitting at a still starting point.
        duration: `${(1.6 + Math.random() * 1.6).toFixed(1)}s`,
        delay: `-${(Math.random() * 1.2).toFixed(1)}s`,
        color: SENDING_HEXAGON_COLORS[i % SENDING_HEXAGON_COLORS.length],
    };
});

/**
 * Chat panel for a vfs-backed (git-free) project — routes the conversation
 * to adk-be-app's code-generation agent instead of mia-flow-be, using the
 * `@mia-platform-internal/chatbot-widget` package for the whole UI (message
 * list, session history, playbook awareness, SSE streaming, tool-call
 * rendering) instead of hand-rolling a new chat path in App.jsx.
 *
 * adk-be-app writes generated code directly to vfs-service; this widget's
 * SSE stream is narration/UI only — the file tree/editor pick up the actual
 * code changes via the separate, already-working vfs-service <-> IndexedDB
 * live-sync mechanism (see src/utils/vfsClient.js), not through this panel.
 *
 * `x-mia-vfs-project` / `x-mia-vfs-service` must match exactly what
 * adk-be-app's src/agent/vfs_tools.py reads (VFS_PROJECT_HEADER /
 * VFS_SERVICE_HEADER) to scope tool calls to the right vfs-service project
 * and, within it, the right service subfolder.
 */
// vfs-service tree/microservices calls default to this revision — every
// other navigate() in App.jsx that builds a /code/... path the same way
// (e.g. its Preview handoff) uses the same literal, there being no per-chat
// branch selector reachable from here.
const DEFAULT_REVISION = 'main';

// Voice (see `chatWidgetConfig`): on unless the build says otherwise; read answers aloud in the language of the UI.
const VOICE_ENABLED = import.meta.env.VITE_VOICE_ENABLED !== 'false';
const VOICE_BY_LANGUAGE = { en: 'en-US-Chirp3-HD-Aoede', it: 'it-IT-Chirp3-HD-Aoede' };
const VOICE_NAME = VOICE_BY_LANGUAGE[(globalThis.navigator?.language || 'en').slice(0, 2).toLowerCase()];

// A vfs_* tool call's response comes in one of two shapes depending on
// whether adk-be-app served it as a NATIVE tool (vfs_ensure_project,
// vfs_create_project, vfs_scaffold_frontend, vfs_get_preview_url — plain
// Python dicts of the form `{success: bool, error: string|null, ...}`) or
// as a real vfs-mcp-server MCP tool (vfs_write_file, vfs_patch,
// vfs_tree_json/tree, vfs_grep, vfs_exec, vfs_bash, vfs_git_status/commit,
// vfs_clone, vfs_import_archive — everything the migration/vfs-adk-generation
// tool migration moved off the native path). MCP tool results have no
// `success` field at all: a successful call is `{content: [{type: 'text',
// text: ...}]}` (see vfs-service's own mcpServer.js `text()` helper), and a
// failed one is flagged with `isError: true` alongside that same `content`
// shape (the MCP SDK sets this automatically on a server-side exception) —
// never a `success: false`. Checking `.response?.success` alone (as every
// vfs_* tool call could until that migration) silently reads every MCP
// tool's response as a failure, since `success` is simply absent — this is
// what broke `previewFilesUpdated` dispatch for `vfs_write_file`/`vfs_patch`
// after that migration, live-confirmed: the service dropdown stopped
// picking up a service the agent created in the background because the
// refresh this depends on never fired.
const isVfsToolCallSuccess = (response) => isToolPayloadSuccess(response);
// Tools that change the app's files: a turn that ran one of them successfully is a change applied (analytics).
const CHANGING_TOOL = /^vfs_(write_file|patch|scaffold_\w+|rm|mv|mkdir|rollback|import_archive|clone|git_pull|git_checkout)$/;

// The widget's own bundled CSS declares its `--app-*` fallback tokens once,
// on `:root` (e.g. `--app-bg-layout: var(--ant-color-bg-layout, #f5f5f5)`),
// but ConfigProvider's cssVar:true output only ever attaches the real
// `--ant-color-*` custom properties to its own nested wrapper node, never to
// `:root` itself. A var()-of-a-var() resolves using the referenced
// variable's value AT THE ELEMENT WHERE IT'S DECLARED (here, :root) — since
// `--ant-color-bg-layout` is undefined there, `--app-bg-layout` freezes at
// its `#f5f5f5` light fallback and simply inherits down unchanged, no
// matter how the nested ConfigProvider is themed. Confirmed live: the
// widget's own container stayed light grey in dark mode even though the
// antd tokens read directly (no `--app-*` indirection) were correctly dark
// everywhere else. Mirroring the real resolved tokens onto `:root`
// ourselves is what actually unfreezes that lookup.
function ChatWidgetRootTokenSync() {
    const { token } = antdTheme.useToken();
    useEffect(() => {
        const root = document.documentElement;
        const vars = {
            '--ant-color-bg-layout': token.colorBgLayout,
            '--ant-color-bg-container': token.colorBgContainer,
            '--ant-color-fill-tertiary': token.colorFillTertiary,
            '--ant-color-border': token.colorBorder,
            '--ant-color-primary-bg': token.colorPrimaryBg,
            '--ant-color-text-secondary': token.colorTextSecondary,
            '--ant-color-text-tertiary': token.colorTextTertiary,
            '--ant-color-text': token.colorText,
            '--ant-color-primary': token.colorPrimary,
            '--ant-color-warning': token.colorWarning,
            '--ant-color-warning-bg': token.colorWarningBg,
            '--ant-color-warning-border': token.colorWarningBorder,
            '--ant-color-warning-text': token.colorWarningText,
            '--ant-color-error': token.colorError,
            '--ant-color-error-bg': token.colorErrorBg,
            '--ant-color-error-border': token.colorErrorBorder,
            '--ant-color-error-text': token.colorErrorText,
        };
        for (const [key, value] of Object.entries(vars)) {
            if (value) root.style.setProperty(key, value);
        }
        return () => {
            for (const key of Object.keys(vars)) {
                root.style.removeProperty(key);
            }
        };
    }, [token]);
    return null;
}

export default function VfsChatPanel({ vfsProjectId, vfsServiceName, initialMessage, pageContext, pendingMessage, pendingDraft, tenantId, onNewConversation, onNewChatInProject, onFreshConversationStarted, onShowExternalPreview, onProjectRedirect, onProjectBound, onActivityStart, onTurnComplete, onOpenFile, onCodeReady, headerActions, previewEmbedded, hideCanvas, initialPlaybookId, initialConversationId }) {
    const { user } = useAuth();
    const userId = user?.sub || user?.id || user?.email;
    const navigate = useNavigate();
    const location = useLocation();
    // Memories navigates here with ?conversationId=<real ADK session id> to
    // resume a specific past conversation (see pages/Memories.jsx) — takes
    // priority over the deterministic per-project/service id below, which is
    // only ever a default, not the one true session for this project.
    const [searchParams] = useSearchParams();
    const requestedSessionId = searchParams.get('conversationId');
    // Live-reported: before vfsProjectId resolves (the window between a
    // brand-new Home conversation starting and its first vfs_ensure_project
    // response), the naive `vfs-${vfsProjectId}` fallback template literal
    // still runs — with vfsProjectId undefined/null, JS happily stringifies
    // that into the literal "vfs-undefined"/"vfs-null", which the widget
    // then tries to resume on mount: a real, always-guaranteed-to-404
    // GET .../sessions/vfs-null, confirmed live in the console. Only
    // compute a fallback id once there's a real project to derive it from;
    // `undefined` here means "no id yet", which useChat's own mount effect
    // already handles correctly (skips the resume attempt entirely rather
    // than firing it against a session that can't possibly exist).
    //
    // `initialConversationId` (Home's embedded door only — no URL of its
    // own to carry `?conversationId=` through) takes priority over the
    // vfsProjectId-derived guess for the same reason `requestedSessionId`
    // does: without it, the REAL session id this conversation ends up
    // running under is whatever useChat.ts generates internally the first
    // time a message is actually sent (`sessionIdRef.current = initialSessionId
    // ?? randomId()`) — a value the host never learns until
    // vfs_set_project_metadata happens to have already written it back into
    // .mia/project.json's conversationId field. Live-reported real bug:
    // clicking "Code" in the window between the project being created
    // (vfs_ensure_project — Code becomes enabled) and that metadata write
    // (vfs_set_project_metadata — typically called later, once the app's
    // purpose is clear) carried the WRONG id into Code mode's URL, 404ing
    // and silently starting a brand-new, empty conversation instead of
    // resuming the one actually in progress. Passing Home's own
    // up-front-generated, stable id here means useChat seeds FROM it
    // instead of generating its own — so it simply IS the real session id
    // from the very first message, with no window where they can diverge.
    const derivedSessionId = requestedSessionId || initialConversationId || (vfsProjectId ? `vfs-${vfsProjectId}` : undefined);

    // Picked once per mount, shown as clickable suggestion chips on the
    // widget's own start screen (see ChatWidgetOverview.prompts — "Clicking
    // one sends it") — short, directly-typeable prompts (mostly brain/
    // memory discovery), separate from Home's longer educational tips.
    const [chatSuggestions] = useState(() => getRandomSuggestions('chat', 5));

    // Result surface for the commands below (`/project`, `/service`, `/run`,
    // `/runs`, `/cancel`, `/files`) — see CommandResultModal's own doc for why
    // these can't just write into the widget's transcript the way a chat
    // reply would. `/conversations`, `/resume`, `/new` and `/clear` are NOT
    // here: mia-chatbot-widget implements those itself as built-in commands
    // (same real ADK session history it already fetches for its own history
    // modal), so this app only has to supply what only it — an app that
    // knows about Mia Platform projects/services/workflows/vfs-service —
    // can implement.
    const [commandResult, setCommandResult] = useState(null);
    const closeCommandResult = useCallback(() => setCommandResult(null), []);

    // Fed by the widget's `onToolCall` (mia-chatbot-widget's ChatWidgetProps,
    // added specifically for this) — a separate, always-visible live view of
    // the same tool-call activity the chat transcript already renders inline,
    // so the user doesn't have to scroll back through the conversation to see
    // what the agent is doing right now. Keyed by id in a ref so a later
    // "response" update replaces the matching "call" entry in place instead of
    // appending a second row for the same call.
    const [toolCalls, setToolCalls] = useState([]);
    const toolCallIndexRef = useRef(new Map());
    // Stamped onto each tool call the instant it's first seen (never
    // updated afterward) so WorkflowTimeline can group calls by the turn
    // they belong to — incremented in handleTurnComplete below. There's no
    // ordering signal from the widget finer than "which turn", so this is
    // the realistic grouping granularity (see task doc for why exact
    // Claude-Code-style multi-group-per-turn isn't achievable here).
    const turnIndexRef = useRef(0);
    // Analytics: whether the turn in progress changed files, and which preview/rollback tool calls were already reported.
    const turnChangedRef = useRef(false);
    const previewReportedRef = useRef(new Set());
    const [isTimelineOpen, setIsTimelineOpen] = useState(false);

    // Which playbook this chat talks to. "auto" (the default) means "pick
    // the best one from what the user actually asks for" — see
    // PlaybooksService.selectPlaybookServerSide (task #194: a real
    // server-side LLM call, not just local keyword overlap) — instead of
    // always hardcoding "code-generation-playbook" the way this panel used
    // to. Persisted per
    // project+service so re-opening the same project's chat later keeps
    // using whatever was picked (either explicitly, or resolved by Auto)
    // rather than re-asking or silently reverting to the default.
    const playbookChoiceKey = `mia_flow_playbook_choice::${vfsProjectId}::${vfsServiceName || ''}`;
    const resolvedPlaybookKey = `mia_flow_resolved_playbook::${vfsProjectId}::${vfsServiceName || ''}`;
    const [playbooks, setPlaybooks] = useState([]);
    // Playbooks left out by the person's plan (docs/SANDBOX_FIRST.md): the picker stays on Auto, which then answers
    // with the default assistant (mia-flow-bff's /api/playbooks/select fallback). Unknown counts as included.
    const planFeatures = usePlanFeatures();
    // The person's credits: a warning while they run low, and the chat waits when they are used up and enforced.
    const credits = useChatCredits();
    const creditsHold = credits.hold;
    // AI Foundry off for this person (not enabled by the organization, not in their plan, or they chose their own key):
    // no Catalog playbooks, no Auto pick, Flow's default playbook answers, on the person's own model key.
    const aiFoundryOff = !planFeatures.aiFoundry;
    // Playbooks follow the AI Foundry connection; the model key follows the person's choice (see usePlanFeatures).
    const onOwnKey = Boolean(planFeatures.ownKeyChat);
    const ownKeyState = useOwnModelKey(onOwnKey);
    // The widget is mounted once the plan is known and, on the person's own key, the key is saved (see the render).
    const widgetMounted = planFeatures.ready && !(onOwnKey && ownKeyState !== 'saved');
    // A `pendingMessage` (Fix with AI, Ask AI) that is already there when the widget mounts — Chat was just turned on
    // by that click, or the panel just remounted — is held back until the widget has opened its conversation
    // (`onSessionChange`, or PENDING_MESSAGE_SETTLE_MS at the latest). Handed over at once, the widget sent it into a
    // new session and then loaded `initialSessionId` over it: live, the message never reached the agent.
    const [widgetSettled, setWidgetSettled] = useState(false);
    useEffect(() => {
        if (!widgetMounted) { setWidgetSettled(false); return undefined; }
        const timer = setTimeout(() => setWidgetSettled(true), PENDING_MESSAGE_SETTLE_MS);
        return () => clearTimeout(timer);
    }, [widgetMounted]);
    // Set by handleNewChat when a fresh conversation was started inside a project: the first real session id the
    // widget reports afterwards is that conversation's, handed to the host so it can put it in the URL.
    const awaitingFreshSessionRef = useRef(false);
    const handleSessionChange = useCallback((sessionId) => {
        setWidgetSettled(true);
        if (sessionId && awaitingFreshSessionRef.current) {
            awaitingFreshSessionRef.current = false;
            onFreshConversationStarted?.(sessionId);
        }
    }, [onFreshConversationStarted]);
    // A `pendingDraft` (the Data tab's "Ask the AI to add it", utils/askAiInChat.js): the text is written into the
    // widget's input, not sent: the widget has no draft prop, so it goes into its textarea (prefillChatInput) once the
    // widget has settled and its input is there (looked for every PREFILL_RETRY_MS, PREFILL_GIVE_UP_MS at most).
    const chatContainerRef = useRef(null);
    const prefilledIdRef = useRef(null);
    useEffect(() => {
        if (!pendingDraft?.id || !pendingDraft.text || !widgetSettled || prefilledIdRef.current === pendingDraft.id) { return undefined; }
        let tries = 0;
        let timer = null;
        const attempt = () => {
            if (prefillChatInput(chatContainerRef.current, pendingDraft.text)) {
                prefilledIdRef.current = pendingDraft.id;
                return;
            }
            tries += 1;
            if (tries * PREFILL_RETRY_MS < PREFILL_GIVE_UP_MS) { timer = setTimeout(attempt, PREFILL_RETRY_MS); }
        };
        attempt();
        return () => clearTimeout(timer);
    }, [pendingDraft, widgetSettled]);
    // Live-confirmed bug: with no real vfsProjectId yet (any fresh, project-
    // less chat — the plain /chat door before the agent has created
    // anything), BOTH keys above collapse to the exact same literal string
    // (`...::undefined::`) for every such conversation, regardless of what
    // it's actually about. Whatever playbook one blank chat happened to
    // resolve/choose got silently stuck and reused for every OTHER blank
    // chat afterward, on this browser, indefinitely — e.g. a much earlier
    // "Catalog Assistant" test permanently hijacked every new chat's
    // default playbook away from Code Generation. Scoping this cache to a
    // real project (`vfsProjectId` truthy) is the actual fix — reading/
    // writing it for a project-less chat never made sense in the first
    // place, since there's no real "this project" to scope the memory to.
    // `?playbookId=` — set by App.jsx's Home auto-select routing and by
    // PlaybookLauncher.jsx's launch() — used to be read by NEITHER of those
    // two things this component actually needs it for. It only ever fed a
    // separate, unrelated legacy system (usePlaybookApplication.js/
    // AdkClient.js's overrideAppName), which this component (the one that
    // actually renders here) never consulted. Live-confirmed bug: landing
    // here via that URL param with no localStorage choice yet and no
    // initialMessage caused the "auto, no signal" gate below to immediately
    // and permanently lock in DEFAULT_PLAYBOOK_NAME instead of the playbook
    // the user actually picked/was routed to — 404 on the wrong ADK app name,
    // then run_sse cancelled with "Session not found". The Catalog itemId
    // this param carries IS the same slug adk-be-app's own runnable-playbook
    // list keys on (confirmed identical strings for every real playbook), so
    // it can be used directly as an explicit playbookChoice, same as if the
    // user had picked it from the picker themselves.
    // `initialPlaybookId` is the same hand-off signal as `?playbookId=`, for
    // a caller that can't put it in the URL — Home.jsx's embedded chat view
    // renders VfsChatPanel directly (no route/query-string of its own to
    // carry a param through), so it passes the pre-matched playbook via this
    // prop instead. Folded into one hint so every check below stays a single
    // `if`, same as before this existed.
    const playbookIdFromUrl = searchParams.get('playbookId');
    const playbookIdHint = playbookIdFromUrl || initialPlaybookId || null;
    const [playbookChoice, setPlaybookChoiceState] = useState(() => {
        if (!vfsProjectId) { return playbookIdHint || 'auto'; }
        try {
            const stored = localStorage.getItem(playbookChoiceKey);
            if (stored) { return stored; }
            if (playbookIdHint) { return playbookIdHint; }
            return 'auto';
        } catch {
            return playbookIdHint || 'auto';
        }
    });
    // The playbook actually passed to the widget right now — for an explicit
    // choice this equals playbookChoice; for "auto" it's whatever was last
    // resolved (persisted), or null until either a hand-off message or the
    // gate below resolves one.
    const [resolvedPlaybookName, setResolvedPlaybookName] = useState(() => {
        if (!vfsProjectId) { return playbookIdHint || null; }
        try {
            const stored = localStorage.getItem(playbookChoiceKey);
            if (stored && stored !== 'auto') {
                return stored;
            }
            if (!stored && playbookIdHint) {
                return playbookIdHint;
            }
            return localStorage.getItem(resolvedPlaybookKey) || null;
        } catch {
            return playbookIdHint || null;
        }
    });

    // Persist the hand-off choice once a real project id exists, so it
    // survives beyond the URL param's/prop's own lifetime (cleared by
    // navigation, or simply absent on a later resume of the same
    // conversation) — same key `setPlaybookChoice` itself writes, so this is
    // a one-time seed, not a parallel mechanism.
    useEffect(() => {
        if (!vfsProjectId || !playbookIdHint) { return; }
        try {
            if (!localStorage.getItem(playbookChoiceKey)) {
                localStorage.setItem(playbookChoiceKey, playbookIdHint);
            }
        } catch {
            // best-effort persistence only
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vfsProjectId, playbookIdHint, playbookChoiceKey]);

    useEffect(() => {
        // Not before the plan says whether AI Foundry is in use, and never with it off.
        if (!planFeatures.ready) { return undefined; }
        if (aiFoundryOff) {
            setPlaybooks([]);
            return undefined;
        }
        let cancelled = false;
        listPlaybooks().then((list) => {
            if (!cancelled) setPlaybooks(list);
        }).catch((error) => {
            // eslint-disable-next-line no-console
            console.warn('⚠️ Failed to load playbooks list:', error);
        });
        return () => { cancelled = true; };
    }, [aiFoundryOff, planFeatures.ready]);

    const setPlaybookChoice = useCallback((name) => {
        setPlaybookChoiceState(name);
        if (vfsProjectId) {
            try {
                localStorage.setItem(playbookChoiceKey, name);
            } catch {
                // best-effort persistence only
            }
        }
        if (name !== 'auto') {
            setResolvedPlaybookName(name);
        } else {
            // Re-entering Auto for this project/service — drop any previously
            // resolved playbook so the gate (or the next hand-off message)
            // gets to pick again instead of silently reusing a stale one.
            setResolvedPlaybookName(null);
            if (vfsProjectId) {
                try {
                    localStorage.removeItem(resolvedPlaybookKey);
                } catch {
                    // best-effort
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playbookChoiceKey, resolvedPlaybookKey, vfsProjectId]);

    // Resuming an EXISTING session (`?conversationId=` present — a saved
    // chat, not a brand-new one) must never re-run Auto classification at
    // all: the playbook this conversation actually talked to was already
    // decided once, the first time it resolved, and re-classifying is both
    // a slow, wasted LLM call AND a real risk of landing on a DIFFERENT
    // playbook than the one this conversation's history was actually built
    // under. The previous project+service-scoped localStorage cache
    // (`resolvedPlaybookKey`) almost covered this, but isn't truly "saved
    // in the session" — it's this browser's cache, lost on a different
    // device or cleared storage, and not scoped to one specific
    // conversation if a project ever has more than one. Read it straight
    // from `.mia/project.json` instead (written by the persistence calls
    // below, the first time any session resolves a playbook) — real,
    // server-side, per-project session state.
    const [sessionMetadataMiss, setSessionMetadataMiss] = useState(false);
    const sessionMetadataStartedRef = useRef(false);
    useEffect(() => {
        if (!requestedSessionId || !vfsProjectId || resolvedPlaybookName) { return undefined; }
        if (sessionMetadataStartedRef.current) { return undefined; }
        sessionMetadataStartedRef.current = true;
        let cancelled = false;
        readProjectMetadata(vfsProjectId).then((meta) => {
            if (cancelled) return;
            if (meta?.resolvedPlaybookName) {
                setResolvedPlaybookName(meta.resolvedPlaybookName);
            } else {
                setSessionMetadataMiss(true);
            }
        }).catch(() => {
            if (!cancelled) setSessionMetadataMiss(true);
        });
        return () => { cancelled = true; };
    }, [requestedSessionId, vfsProjectId, resolvedPlaybookName]);

    // Auto resolves the moment there's real text to classify against —
    // whichever arrives first: a hand-off message (Home's prompt, Canvas's
    // "Ask AI", etc., landing in `initialMessage`) is classified right away.
    // With nothing to classify yet (a fresh chat opened with no hand-off
    // text) it falls back to DEFAULT_PLAYBOOK_NAME immediately — the exact
    // behavior this panel always had — rather than blocking the widget from
    // rendering at all: the user still types their actual first message
    // into the real chat widget UI, same as before Auto existed. There is
    // deliberately no separate "tell me what you want first" screen here;
    // that would mean re-implementing the widget's own input/send affordance
    // a second time just to peek at the text before the widget sees it.
    //
    // task #194 — the actual classification is now a real server-side LLM
    // call (adk-be-app's POST /api/playbooks/select, see
    // PlaybooksService.selectPlaybookServerSide) instead of the old local
    // keyword-overlap heuristic; the UI still shows "Auto" either way, this
    // only changed how Auto resolves under the hood.
    useEffect(() => {
        if (aiFoundryOff || playbookChoice !== 'auto' || resolvedPlaybookName || playbooks.length === 0) {
            return;
        }
        const persist = (name) => {
            if (!vfsProjectId) { return; }
            try {
                localStorage.setItem(resolvedPlaybookKey, name);
            } catch {
                // best-effort
            }
            updateProjectMetadata(vfsProjectId, { resolvedPlaybookName: name }).catch(() => {
                // best-effort — a later session/device just falls back to
                // classifying again, same as before this existed
            });
        };
        if (!initialMessage) {
            // REAL BUG, live-confirmed hang ("Currenciting..." overlay stuck
            // forever, starting a brand-new conversation from Home): this
            // branch used to gate on `requestedSessionId` alone to decide
            // "existing session, wait for the saved playbook instead of
            // defaulting" — but Home pre-assigns a `?conversationId=` to
            // the URL for a BRAND-NEW conversation too, before any
            // vfsProjectId/project exists yet. The metadata-lookup effect
            // above can't even START without a vfsProjectId, so
            // `sessionMetadataMiss` never flips, and this effect sat
            // blocked forever waiting for a lookup that was never going to
            // run — total deadlock. Require `vfsProjectId` too: with no
            // project yet, there is nothing to look up, so there is no
            // point waiting — fall straight through to DEFAULT_PLAYBOOK_NAME
            // exactly as this branch always did before today's change.
            if (requestedSessionId && vfsProjectId && !sessionMetadataMiss) {
                return;
            }
            setResolvedPlaybookName(DEFAULT_PLAYBOOK_NAME);
            persist(DEFAULT_PLAYBOOK_NAME);
            return;
        }
        // initialMessage present — always classify right away, regardless
        // of requestedSessionId, for the same reason as the comment above:
        // a conversationId in the URL does not mean a real session/project
        // exists yet for THIS conversation, so gating a fresh hand-off
        // message's classification on a session-metadata lookup risks the
        // exact same deadlock. A genuinely-existing conversation, by
        // definition, has no fresh hand-off text to classify anyway (see
        // the `!initialMessage` branch above, which is where "existing
        // session" is actually handled).
        let cancelled = false;
        selectPlaybookServerSide(playbooks, initialMessage, DEFAULT_PLAYBOOK_NAME).then((best) => {
            if (cancelled) return;
            setResolvedPlaybookName(best);
            persist(best);
        });
        return () => { cancelled = true; };
    }, [aiFoundryOff, playbookChoice, resolvedPlaybookName, initialMessage, playbooks, resolvedPlaybookKey, vfsProjectId, requestedSessionId, sessionMetadataMiss]);

    // Live-confirmed race: the widget renders with `fixedPlaybookName={
    // resolvedPlaybookName || DEFAULT_PLAYBOOK_NAME}` immediately — while
    // Auto's classification above is still an in-flight async call,
    // `resolvedPlaybookName` is null, so the widget starts the FIRST turn
    // (this hand-off `initialMessage`) under DEFAULT_PLAYBOOK_NAME. The
    // instant classification resolves a moment later, `resolvedPlaybookName`
    // flips to the real pick, `fixedPlaybookName` changes, and the widget
    // re-resolves its own `appName` — aborting the just-started run_sse
    // ("Session not found" on whatever came next). Manual playbook selection
    // never hits this: `setPlaybookChoice` sets `resolvedPlaybookName`
    // SYNCHRONOUSLY, so there's no gap to race. Withholding the hand-off text
    // from the widget until classification actually settles (rather than
    // racing it) closes this at the source — the widget then only ever
    // mounts/sends under the FINAL playbook, never the default first.
    const isClassifyingInitialMessage = !aiFoundryOff && playbookChoice === 'auto' && !!initialMessage && !resolvedPlaybookName;
    const widgetPlaybookName = aiFoundryOff ? DEFAULT_PLAYBOOK_NAME : (resolvedPlaybookName || DEFAULT_PLAYBOOK_NAME);
    const effectiveInitialMessage = isClassifyingInitialMessage ? undefined : initialMessage;

    // Cycles the same random-order, no-immediate-repeat word list the
    // widget's own WaitingIndicator uses (see SENDING_WAITING_WORDS' own
    // comment) — only while the overlay below is actually showing, so this
    // never ticks in the background for the rest of a conversation.
    const [sendingWordIndex, setSendingWordIndex] = useState(() => Math.floor(Math.random() * SENDING_WAITING_WORDS.length));
    // Fresh random layout each time this overlay mounts (see makeSendingHexagons' own comment).
    const [sendingHexagons] = useState(makeSendingHexagons);
    useEffect(() => {
        if (!isClassifyingInitialMessage) { return undefined; }
        const interval = setInterval(() => {
            setSendingWordIndex((prev) => {
                if (SENDING_WAITING_WORDS.length <= 1) { return prev; }
                let next = Math.floor(Math.random() * SENDING_WAITING_WORDS.length);
                while (next === prev) { next = Math.floor(Math.random() * SENDING_WAITING_WORDS.length); }
                return next;
            });
        }, SENDING_WAITING_WORD_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [isClassifyingInitialMessage]);

    // task #81 — "the ai can move the user from chat to code for showing the
    // preview": the agent already has a deliberate, explicit action for
    // "show the user what I built" — calling vfs_get_preview_url (it's never
    // invoked for routine file edits, only once the agent decides there's a
    // real, working prototype worth showing — see its own docstring). Its
    // result already reaches this component structurally via onToolCall
    // (mia-chatbot-widget's ToolCallInfo.response, the same channel
    // WorkflowTimeline's toolCalls already render), so riding that instead
    // of inventing a new text-marker protocol in the chat transcript is the
    // lowest-friction option: no adk-be-app change needed at all, just react
    // to the response shape that tool already returns.
    //
    // Only acts when the tool call actually succeeded with a real, ready
    // preview_url (not a "still building"/error response — see
    // vfs_get_preview_url's own {"success": False, ...} branches, which this
    // deliberately ignores). Delegates the actual tab switch to
    // `onShowExternalPreview` (App.jsx -> Canvas.showExternalPreview, see
    // their own comments) rather than navigating by URL: navigating to
    // `/code/.../<service>/main/preview` only works once a console-
    // registered microservice with that name exists, but the very first
    // build in a brand-new vfs-only project — the case this most needs to
    // handle — has none yet ("No services yet" in the Preview tab).
    // showExternalPreview instead pushes the real URL straight into
    // Canvas's own preview-tab state, independent of console config. The
    // service name defaults to "app", matching vfs_get_preview_url's own
    // default when no explicit service_name was passed (see its BE
    // docstring) — this stays correct even before vfsServiceName exists.
    // Each tool-call id is only acted on once, since mia-chatbot-widget
    // re-invokes onToolCall for both the "call" and "response" lifecycle
    // events of the SAME id.
    const previewShownRef = useRef(new Set());
    // Separate from previewShownRef — see the BUG FIX comment at its call
    // site (the `vfs_get_preview_url` handler) for why the resync/
    // onCodeReady chain must not share previewShownRef's dedup.
    const previewResyncedRef = useRef(new Set());
    // Dedup for the "agent wrote a file, switch to Code" trigger below —
    // same one-id-acted-on-once reasoning as previewShownRef, kept separate
    // since it gates a completely different call (vfs_write_file/vfs_patch,
    // not vfs_get_preview_url).
    const codeShownRef = useRef(new Set());
    // BUG FIX (live-reported, repeated across multiple fresh test projects
    // after the previewResyncedRef/servicesToSync fixes above — "the same
    // errors, nothing changed"): the resync chain above only ever ran from
    // INSIDE the vfs_get_preview_url tool-call handler, gated on that exact
    // tool being called AND returning `{success: true, preview_url}`. In
    // several live test runs, no `forceResyncServiceFromVfs: done` log line
    // ever appeared for service "." at all — meaning that handler branch
    // never fired for those turns (agent didn't call vfs_get_preview_url
    // this turn, called it with a different response shape, or it hasn't
    // resolved by the time the user checks). Tool-call-shape dependence is
    // exactly the class of bug this file's own handleTurnComplete/
    // pendingRedirectRef comments already warn about elsewhere ("wait for
    // the real turn-complete signal, not a tool-call heuristic"). Extracted
    // so handleTurnComplete (below) — which the vendored ChatWidget's
    // `onTurnComplete` fires unconditionally at the end of EVERY turn,
    // regardless of which tools ran — can call the exact same resync chain
    // as a guaranteed fallback, independent of vfs_get_preview_url ever
    // being invoked at all.
    const triggerPostTurnResync = useCallback((resyncProjectId, resolvedServiceName) => {
        if (!resyncProjectId) { return; }
        // ROOT CAUSE (finally found it — every previous fix in this chain
        // was patching code that could never run): forceResyncServiceFromVfs
        // silently no-ops, with NO log line at all, unless
        // vfsClient.isVfsBackedProject(resyncProjectId) is true — which
        // reads a localStorage allowlist written by markProjectVfsBacked().
        // That function is called from Canvas.jsx, quickProjectCreation.js
        // and gitTemplateClone.js — NEVER from this file. On the plain
        // /chat door (hideCanvas=true), Canvas.jsx never mounts, so a
        // project created purely through agent chat was NEVER marked
        // vfs-backed, and every resync attempt from either trigger point
        // above (the vfs_get_preview_url handler or handleTurnComplete)
        // silently did nothing for the entire chat session — explaining why
        // no `forceResyncServiceFromVfs: done` log line ever appeared in
        // any live test, regardless of which code path led here. Mark it
        // here, right before the one call site that actually needs it to be
        // true, so every project this component ever resyncs is guaranteed
        // vfs-backed by the time forceResyncServiceFromVfs checks — a
        // project created via vfs_create_project always is one, there is no
        // other kind on this door.
        markProjectVfsBacked(resyncProjectId);
        window.dispatchEvent(new CustomEvent('vfsForceResync', {
            detail: { projectId: resyncProjectId, serviceName: resolvedServiceName },
        }));
        addPendingVfsResync(resyncProjectId);
        const realServiceNames = getVfsServiceNames(resyncProjectId);
        const servicesToSync = Array.from(new Set([
            ...(realServiceNames.length > 0 ? realServiceNames : [resolvedServiceName || '.']),
            '.',
        ]));
        return Promise.all(
            servicesToSync.map((name) => forceResyncServiceFromVfs(resyncProjectId, name, { forceRefresh: true }).catch(() => {
                // Best-effort — onCodeReady still fires below so Code isn't
                // stuck disabled forever on a transient vfs-service hiccup;
                // a later Refresh/WS sync still self-heals it.
            }))
        ).then(() => {
            onCodeReady?.();
        });
    }, [onCodeReady]);
    // Debounces a `previewFilesUpdated` dispatch to ~2s after the last
    // vfs_write_file/vfs_patch_file tool call in a burst (an agent building
    // a new project writes many files back-to-back) — see handleToolCall.
    const vfsWriteRefreshTimerRef = useRef(null);
    // task #89 — "why if from chat i create a project is not visible when i
    // switch to code / why it doesn't switch automatically": a session
    // arriving here (whether from the Home hero or `handleResetConversation`)
    // ALREADY has its own vfs project silently created by the FE
    // (`vfsProjectId` below) before the user's first message ever reaches
    // adk-be-app. If the agent then ALSO calls `vfs_create_project` this turn
    // (e.g. because the user's message itself literally says "create a
    // project…", which is exactly its documented trigger), that creates a
    // SECOND, different project — the id in `toolCall.response.project_id` —
    // that this session's URL/canvasProjectContext knows nothing about. The
    // agent's own tool docstring (adk-be-app's vfs_create_project) is explicit
    // that the retargeting it does internally only lasts for the rest of that
    // one turn: "the session/URL still needs to be pointed at this new
    // project id for anything after this turn to keep targeting it" — so the
    // FE must do exactly that here, the moment the tool call comes back
    // successful, rather than leaving the user stranded on the old project.
    // Mirrors the same (tenantId, projectId) -> `/code/...` navigate() the
    // `/project` slash command below already uses to switch projects.
    const projectSwitchedRef = useRef(new Set());
    // task #142 — real regression: the vfs_create_project auto-redirect
    // above was written for #89's scenario (a brand-new, still-EMPTY FE-
    // pre-created vfs project, never written to yet). It fires on
    // ANY divergence between a vfs_create_project response and the current
    // vfsProjectId — including mid-conversation, in an ALREADY-established
    // project with real prior work (files written, a preview already
    // built). Confirmed live: a later turn's spurious/duplicate
    // vfs_create_project call silently redirected the whole UI away from a
    // working project (real preview showing) to a brand-new empty one —
    // the chat transcript kept the old project's messages (already
    // rendered), but every NEW message afterward (and the Preview panel)
    // pointed at the new, empty project instead, producing "vfs-service
    // returned no files" build failures while the agent's own prior replies
    // still referenced the real, working project. Track whether THIS
    // session has already seen a successful write against the current
    // vfsProjectId — if so, a later vfs_create_project divergence is far
    // more likely a spurious/duplicate call than a legitimate first-time
    // creation, so don't auto-redirect (the tool's own response still tells
    // the agent about the new project id for that one turn, per its
    // docstring — only the FE's automatic navigation is suppressed here).
    const hasEstablishedActivityRef = useRef(false);
    // Bridges the gap between "vfs_create_project just registered a default
    // service" and "the FE has actually picked that up" — the two are NOT
    // the same instant. `registerVfsService` below writes the default to
    // localStorage synchronously, but App.jsx only ever reads it back into
    // this component's `vfsServiceName` PROP on the NEXT mount, which
    // `handleTurnComplete` deliberately defers until the whole streaming
    // turn is over (see pendingRedirectRef's doc — redirecting mid-turn
    // kills the SSE stream). So for every tool call the agent makes in the
    // REST of the turn that just created the project — e.g. the
    // `vfs_write_file` calls that actually write the new project's code —
    // `vfsServiceName` is still whatever it was before creation (typically
    // undefined/empty for a Home-started, no-project-yet conversation), and
    // `buildHeaders` below would omit `x-mia-vfs-service` entirely.
    // Live-confirmed: adk-be-app's own tool docstring says vfs_create_project
    // retargets its OWN project scoping for the rest of that turn (which is
    // why `x-mia-vfs-project` keeps working), but nothing does the
    // equivalent for the service — every subsequent same-turn
    // `vfs_write_file` failed with "No service is selected for this
    // session", repeatedly, even though the URL/preview correctly showed
    // the new service by the time the turn (and the deferred redirect)
    // finished. Recording the just-registered default here — read by
    // `buildHeaders` as a same-turn fallback — closes that gap without
    // touching the deferred-redirect design the comments above depend on.
    const pendingServiceNameRef = useRef(null);
    // Same problem, for `vfsProjectId` itself this time: on the plain /chat
    // door, `vfsProjectId` (the PROP) stays undefined for the whole
    // conversation — adk-be-app rebinds the MCP session's own project scope
    // server-side once vfs_create_project/vfs_ensure_project runs (see the
    // comment above on why `x-mia-vfs-project` keeps working regardless),
    // but nothing updates this component's OWN prop to match; App.jsx only
    // ever learns the real id via `onProjectBound`, one-directional, never
    // passed back down. Live-confirmed: WorkflowTimeline's diff drill-down
    // (uses `vfsProjectId` to call vfsClient.getCommitDiff directly, no
    // agent/MCP involved) always reported "No diff available" on /chat
    // because of exactly this. Same fix as pendingServiceNameRef: record
    // the real id the instant this turn learns it, and prefer that over the
    // stale/undefined prop wherever THIS component needs the real project
    // id for something the prop alone can't answer.
    const pendingProjectIdRef = useRef(null);
    // `vfsServiceName`/`vfsProjectId` (the PROPS) mirrored into refs so
    // `buildHeaders` below can read the LATEST value at call time without
    // itself being recreated (and therefore without `config` below needing
    // to be recreated) on every prop change. Plain unconditional ref writes
    // in the render body, same pattern as pendingServiceNameRef/
    // pendingProjectIdRef above — safe before any early return.
    const vfsServiceNameRef = useRef(vfsServiceName);
    vfsServiceNameRef.current = vfsServiceName;
    const vfsProjectIdRef = useRef(vfsProjectId);
    vfsProjectIdRef.current = vfsProjectId;
    // Stable across every re-render of this component (reads current values
    // from refs at CALL time, not from closed-over props) — previously a
    // brand-new closure on every render, including every tool-call-triggered
    // re-render mid-turn (very frequent, via handleToolCall's setToolCalls).
    // If the vendored widget's internals ever compare its `config` prop by
    // reference, that per-render churn could look like "config changed,
    // reset" — a plausible contributor to an intermittent, no-remount
    // transcript reset observed live (ruled out: a stray navigate(), and a
    // key-prop remount — a mountId trace showed exactly one mount through
    // the reproduced reset). Stabilizing this closure, and memoizing
    // `chatWidgetConfig` below on it, removes that churn regardless of
    // whether it's the true root cause — an unstable prop identity handed to
    // a child is never desirable on its own.
    const buildHeaders = useCallback(() => {
        const resolvedServiceName = vfsServiceNameRef.current || pendingServiceNameRef.current;
        // Live-confirmed: on the plain /chat door, `vfsProjectId` (the PROP,
        // mirrored into vfsProjectIdRef) stays undefined for the whole
        // conversation until onProjectBound eventually fires (see
        // pendingProjectIdRef's own doc above) — this used to send that
        // `undefined` straight through as the literal header value, which
        // becomes the four-character string "undefined" once fetch/Headers
        // stringifies it. adk-be-app's vfs-mcp token minting treats that as
        // a real (truthy) project id and asks vfs-service for a token
        // scoped to a project literally named "undefined", which 400s —
        // McpToolset then drops every vfs_* MCP tool for the whole turn
        // with no user-visible signal, and the model, still following
        // instructions that describe those tools as always available,
        // hallucinates a call to one of them ("Tool 'vfs_read_file' not
        // found"). Falling back to pendingProjectIdRef here (same pattern
        // already applied to x-mia-vfs-service just below) and omitting the
        // header entirely when there's still no real id closes the gap.
        const resolvedProjectId = vfsProjectIdRef.current || pendingProjectIdRef.current;
        return {
            ...buildAclContextHeaders(),
            ...(resolvedProjectId ? { 'x-mia-vfs-project': resolvedProjectId } : {}),
            ...(resolvedServiceName ? { 'x-mia-vfs-service': resolvedServiceName } : {}),
        };
    }, []);
    // Declared here (not next to where it's used, just above the JSX return)
    // because every hook must run before this component's early `!userId`
    // return below — see buildHeaders' comment for why this needs to be
    // referentially stable across incidental re-renders.
    const isDark = (localStorage.getItem('theme') || 'light') === 'dark';
    const derivedSessionIdRef = useRef(derivedSessionId);
    derivedSessionIdRef.current = derivedSessionId;
    // A long attached document is kept as files in the project (brain/inputs/...) and the agent is
    // pointed at them, instead of inlining the whole text into the conversation where every later model
    // call would carry it again. Before the project exists the files are staged for this session and
    // adk-be-app's vfs_ensure_project moves them in. Null (not stored) makes the widget inline the text.
    const storeAttachment = useCallback((attachment, file) => storeChatAttachment({
        attachment,
        file,
        projectId: vfsProjectIdRef.current || pendingProjectIdRef.current,
        sessionId: derivedSessionIdRef.current,
    }), []);
    // Binary documents are stored as-is and NOT converted in the browser: the message goes out at once and
    // the agent converts the file on the server (vfs_parse_document), instead of the user waiting on a
    // "Processing attachment…" screen while Docling extracts a large PDF. Null falls back to converting here.
    const deferDocumentConversion = useCallback((file) => deferChatDocument({
        file,
        projectId: vfsProjectIdRef.current || pendingProjectIdRef.current,
        sessionId: derivedSessionIdRef.current,
    }), []);
    // The user began on Home with hands-free voice: keep listening here, no click.
    const [voiceHandoff, setVoiceHandoff] = useState(() => VOICE_ENABLED && takeVoiceHandoff());
    useEffect(() => onVoiceHandoff(() => { if (VOICE_ENABLED && takeVoiceHandoff()) { setVoiceHandoff(true); } }), []);
    // A remount (switching Chat <-> Code changes the layout) must not end a hands-free conversation: while the mode is on,
    // leaving hands the mode to the next chat panel. The check on mount runs after the old panel's cleanup in the same commit.
    const voiceActiveRef = useRef(false);
    // The start is one-shot: once the user has entered or left the mode (including the automatic start itself), the
    // request is spent, so a later remount of the widget never starts listening unless the mode really was on.
    const onVoiceModeChange = useCallback((active) => {
        voiceActiveRef.current = active;
        setVoiceHandoff(false);
    }, []);
    useEffect(() => {
        if (VOICE_ENABLED && takeVoiceHandoff()) { setVoiceHandoff(true); }
        return () => { if (voiceActiveRef.current) { offerVoiceHandoff(); } };
    }, []);
    const chatWidgetConfig = useMemo(() => ({
        apiBaseUrl: config.vfsAgentUrl,
        theme: isDark ? 'dark' : 'light',
        userId,
        buildHeaders,
        storeAttachment,
        deferDocumentConversion,
        // Voice: a microphone in the composer and a speaker on each answer (adk-be-app's /api/audio routes). Turn off with
        // VITE_VOICE_ENABLED=false. The speech-to-text language is detected by the service; the voice follows the UI language.
        enableVoice: VOICE_ENABLED,
        onVoiceModeChange,
        ...(voiceHandoff ? { autoStartVoice: true } : {}),
        ...(VOICE_NAME ? { voiceName: VOICE_NAME } : {}),
    }), [isDark, userId, buildHeaders, storeAttachment, deferDocumentConversion, voiceHandoff, onVoiceModeChange]);
    // The widget's Stop button only aborts its own stream. adk-be-app detaches a run from the HTTP
    // connection on purpose (navigating away must not kill it), so without this the agent keeps
    // working after Stop. Tell the backend to cancel the session's run as well (graceful: it stops at
    // its next safe checkpoint). Capture phase + selector, because the widget exposes no onStop prop.
    useEffect(() => {
        const onStopClick = (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (!target || !target.closest('button[class*="stopButton"]')) { return; }
            if (!derivedSessionIdRef.current) { return; }
            // The {app} URL segment is ignored by the backend (any literal works), same as the Kanban Stop.
            cancelAdkSession('_', userId, derivedSessionIdRef.current).catch(() => {});
        };
        document.addEventListener('click', onStopClick, true);
        return () => document.removeEventListener('click', onStopClick, true);
    }, [userId]);
    // Live-confirmed regression (found testing a plain Home->Wordle prompt):
    // firing onProjectRedirect()+navigate() IMMEDIATELY on the
    // vfs_create_project tool-call event — while the SAME run_sse turn is
    // still streaming — remounts this whole component (App.jsx keys
    // VfsChatPanel by project id, see its own comment for why that remount
    // must exist for task #89). Remounting destroys the vendored
    // ChatWidget instance, which aborts its in-flight fetch/EventSource to
    // adk-be-app's /run_sse. Starlette/FastAPI's streaming response cancels
    // the request-handling coroutine the moment it detects the client
    // disconnect, so the agent's turn is killed server-side too — any tool
    // call after vfs_create_project in that SAME turn (e.g. the
    // vfs_write_file calls that actually write the project's code) never
    // runs. Symptom matched exactly: project shell created (.mia/project.json
    // only), chat showing nothing, preview stuck on "Starting" forever.
    // Fix: instead of guessing how long the turn needs (a fixed delay, or a
    // quiet-period debounce over tool-call events — either way a timer with
    // no real knowledge of whether the stream is actually done), the
    // redirect now waits for the vendored ChatWidget's own `onTurnComplete`
    // callback (added specifically for this — see mia-chatbot-widget's
    // ChatWidget.tsx: it fires on the true->false edge of `useChat`'s
    // internal `isStreaming`, itself set from a `finally` block covering
    // every exit path of the SSE read loop). That is the actual, correct
    // "the turn is over" signal — not an approximation of it.
    const pendingRedirectRef = useRef(null);
    const handleToolCall = useCallback((toolCall) => {
        // The vfs tools answer inside the MCP envelope; every check below reads the opened object (see utils/toolPayload.js).
        const result = toolPayload(toolCall.response);
        if (CHANGING_TOOL.test(toolCall.name || '') && toolCallStatus(toolCall) === 'done') { turnChangedRef.current = true; }
        // A preview the agent built (or failed to): what first_preview / change_applied report. Once per tool call.
        // A rollback the agent did is an undo too (how far back is not known). Once per tool call.
        if (toolCall.name === 'vfs_rollback' && toolCallStatus(toolCall) === 'done' && isVfsToolCallSuccess(toolCall.response) && !previewReportedRef.current.has(toolCall.id)) {
            previewReportedRef.current.add(toolCall.id);
            undone('unknown');
        }
        // `stack` is the preview-runner's detected stack id, top level of the tool's result.
        if ((toolCall.name === 'vfs_get_preview_url' || toolCall.name === 'vfs_restart_preview') && toolCallStatus(toolCall) !== 'running' && !previewReportedRef.current.has(toolCall.id)) {
            const previewProjectId = vfsProjectId || result?.project_id || pendingProjectIdRef.current;
            const ready = Boolean(result?.success && result?.preview_url);
            if (previewProjectId && (ready || toolCallStatus(toolCall) === 'error' || result?.success === false)) {
                previewReportedRef.current.add(toolCall.id);
                previewResult(previewProjectId, ready, { stack: result?.stack });
            }
        }
        setToolCalls((prev) => {
            const index = toolCallIndexRef.current;
            const existingIndex = index.get(toolCall.id);
            if (existingIndex === undefined) {
                index.set(toolCall.id, prev.length);
                return [...prev, { ...toolCall, turnIndex: turnIndexRef.current }];
            }
            const next = [...prev];
            // BUG FIX: a streaming vfs_write_file/vfs_patch call (ADK 2.10's
            // partial function-call args, see mia-chatbot-widget's
            // ADKPartialArg) reports `partial: true` on every growing
            // snapshot, then the widget OMITS the key entirely on the
            // call's final, complete update (its own documented
            // convention — "absent, not false, once complete"). A blind
            // `{...prev, ...toolCall}` spread only ever ADDS/overwrites
            // keys the new object actually has, so an absent `partial`
            // never clears the one already sitting in `prev` — the call
            // would read as still-streaming forever after it finished.
            // Set it explicitly from the incoming update every time,
            // converting "not present" to a real `false`, instead of
            // trusting the spread to do it.
            next[existingIndex] = { ...next[existingIndex], ...toolCall, partial: toolCall.partial === true };
            return next;
        });

        if (
            toolCall.name === 'vfs_get_preview_url' &&
            result?.success &&
            result?.preview_url
        ) {
            // '.' (vfs-service's own "project root is the app" convention),
            // not 'app' — matches adk-be-app's vfs_get_preview_url default
            // and Canvas.jsx's own root-detection, so this call and the
            // agent tool's own render call converge on the same session
            // instead of racing under two different service keys (confirmed
            // live: mismatched defaults produced two concurrent renders for
            // the same project, one hitting "Text file busy").
            const resolvedServiceName = vfsServiceName || toolCall.args?.service_name || '.';

            if (onShowExternalPreview && !previewShownRef.current.has(toolCall.id)) {
                previewShownRef.current.add(toolCall.id);
                onShowExternalPreview(resolvedServiceName, result.preview_url);
            }

            // BUG FIX (severe, live-reported with real screenshots — App.tsx
            // stayed scaffold in the embedded chat-preview Files tab, AND
            // conversationId never got corrected in the URL, even after the
            // agent finished and this handler visibly ran): this whole
            // resync/onCodeReady chain used to live INSIDE the `if
            // (onShowExternalPreview && !previewShownRef...)` block above —
            // coupled to "have I already shown the floating preview button
            // for this exact toolCall.id", a completely unrelated concern.
            // mia-chatbot-widget can re-invoke onToolCall multiple times for
            // the same id as its response object gets enriched/updated after
            // the first pass already satisfied success+preview_url — once
            // previewShownRef marked that id "shown", every later
            // (potentially more complete) invocation silently skipped this
            // ENTIRE block, including the resync and onCodeReady/
            // correctConversationIdInUrl calls. Deduped separately below by
            // previewResyncedRef, independent of the preview-shown state and
            // of whether onShowExternalPreview is even provided.
            const resyncProjectId = vfsProjectId || result.project_id;
            if (resyncProjectId && !previewResyncedRef.current.has(toolCall.id)) {
                previewResyncedRef.current.add(toolCall.id);
                triggerPostTurnResync(resyncProjectId, resolvedServiceName);
            }

            // A RESUMED conversation (page reload, or opened from a saved
            // link) whose project already existed before this page load
            // never calls vfs_create_project/vfs_ensure_project this turn —
            // the only other place chatBoundProjectId gets set — so without
            // this, the embedded preview panel never renders (gated on
            // chatBoundProjectId) even though a project is clearly in use,
            // and the header's Code button falls back to minting a brand-
            // new vfs project instead of finding the real one.
            // Live-confirmed both symptoms. `project_id` on this response is
            // adk-be-app's own addition for exactly this (vfs_preview_tools.py).
            if (result.project_id && pendingProjectIdRef.current !== result.project_id) {
                pendingProjectIdRef.current = result.project_id;
                onProjectBound?.(result.project_id);
            }
        }

        if (
            // Same resumed-conversation gap as vfs_get_preview_url above —
            // this tool is often the FIRST call of a resumed turn (before
            // any preview is requested), so bind from it too rather than
            // waiting for a preview call that may never come this turn.
            toolCall.name === 'vfs_set_project_metadata' &&
            result?.success &&
            result?.project_id &&
            pendingProjectIdRef.current !== result.project_id
        ) {
            pendingProjectIdRef.current = result.project_id;
            onProjectBound?.(result.project_id);
        }

        // Live-requested: "when files are written in vfs show [the] code
        // after" — the agent's first successful file write is switched to
        // Code the same way a real preview already does (goToCode, via the
        // SAME onShowExternalPreview callback — in Chat mode App.jsx's own
        // handler ignores the serviceName/url args entirely and just
        // switches, see its own comment), instead of waiting for a preview
        // that may never come at all (a plain console program, a backend
        // with no served UI, ...) — live-confirmed exact case: a Java
        // "Hello World" write, asked to "open the code" afterward, got the
        // file's content pasted back as chat TEXT instead of actually
        // landing in Code/Monaco. Chat-mode only (`hideCanvas`) — in Code
        // mode this same callback expects a real serviceName+url to open an
        // EXTERNAL preview tab, which a plain file write has neither of.
        // Only the FIRST write of a turn does anything: once switched, the
        // /chat door's VfsChatPanel instance unmounts (route change), so
        // there's nothing left here to keep triggering on later writes in
        // the same turn — Code mode's own resync already keeps showing them
        // as they arrive.
        //
        // BUG FIX (live-reported, screenshotted: several real vfs_write_file
        // calls all completed with green checkmarks in the timeline, but
        // Chat never switched): `result?.success` is the WRONG
        // check for these two tools specifically — unlike
        // vfs_get_preview_url/vfs_set_project_metadata (which DO return an
        // explicit `{success: true, ...}`), vfs_write_file/vfs_patch
        // (vfs-service's own mcpServer.js) report failure by THROWING, and
        // return bare `{...stat, commitSha, ...}`/diff-apply result on
        // success — no `success` key at all, so this was always undefined
        // and the trigger never fired. Reusing WorkflowTimeline's own
        // toolCallStatus (the exact function that decided the green
        // checkmark shown for these same calls) instead of inventing a
        // second, different "did this succeed" check.
        if (
            hideCanvas &&
            (toolCall.name === 'vfs_write_file' || toolCall.name === 'vfs_patch') &&
            toolCallStatus(toolCall) === 'done' &&
            onShowExternalPreview &&
            !codeShownRef.current.has(toolCall.id)
        ) {
            codeShownRef.current.add(toolCall.id);
            onShowExternalPreview(vfsServiceName || '.', null);
        }

        if (
            // Task #160 — live-confirmed bug: the real ADK tool is named
            // `vfs_patch` (vfs_tools.py), not `vfs_patch_file` — that name
            // never existed, so every patch-based edit (the common path for
            // modifying an existing file, as opposed to a full overwrite via
            // vfs_write_file) silently never matched this condition and
            // never dispatched the refresh event below. The chat would say
            // "changes are live in your preview" while the iframe kept
            // showing stale content until the user manually clicked Refresh.
            // Also cover `vfs_scaffold_frontend`, which writes several files
            // in one call (the initial project scaffold) and has the exact
            // same "nothing tells the preview to refresh" gap.
            (toolCall.name === 'vfs_write_file' ||
                toolCall.name === 'vfs_patch' ||
                toolCall.name === 'vfs_scaffold_frontend') &&
            // vfs_write_file/vfs_patch are real vfs-mcp-server MCP tools as
            // of the vfs-adk-generation migration — see isVfsToolCallSuccess's
            // own doc for why a plain `.success` check no longer works for
            // them (vfs_scaffold_frontend is still native and unaffected,
            // but shares this condition, and the helper handles both shapes).
            isVfsToolCallSuccess(toolCall.response) &&
            vfsProjectId
        ) {
            // Nothing else bridges "agent wrote/patched a vfs file" to
            // Canvas.jsx's own preview-refresh machinery — the existing
            // `previewFilesUpdated`/`diffApplied` listeners there only fire
            // for Monaco-editor-driven or legacy inline-diff changes, never
            // for raw vfs tool calls (confirmed: this file dispatched no
            // custom events at all before this fix). Without this, a fresh
            // Home-created project's first `loadServicePreview` attempt can
            // race the agent's own file-writing (fires before all files
            // exist, or before npm install completes), throw "No files
            // available for preview", and then sit in that error state
            // forever — nothing ever retries it (task #98). Canvas.jsx's
            // `refreshPreview()` already knows how to recover from exactly
            // this: when the active tab has an error, it calls
            // `autoRenderOnPreviewNotFound` for a full re-render rather than
            // just reloading the iframe — it just needed something to call
            // it. Debounced so a burst of file writes only triggers one
            // refresh, ~2s after the last one in the burst.
            // '.' (vfs-service's own "project root is the app" convention),
            // not 'app' — matches adk-be-app's vfs_get_preview_url default
            // and Canvas.jsx's own root-detection, so this call and the
            // agent tool's own render call converge on the same session
            // instead of racing under two different service keys (confirmed
            // live: mismatched defaults produced two concurrent renders for
            // the same project, one hitting "Text file busy").
            const resolvedServiceName = vfsServiceName || toolCall.args?.service_name || '.';
            // Real, successful write against the CURRENT project — from
            // this point on, a later vfs_create_project divergence for this
            // session is a spurious/duplicate call, not a first-time
            // creation (see hasEstablishedActivityRef's own doc above).
            hasEstablishedActivityRef.current = true;
            if (vfsWriteRefreshTimerRef.current) {
                clearTimeout(vfsWriteRefreshTimerRef.current);
            }
            vfsWriteRefreshTimerRef.current = setTimeout(() => {
                window.dispatchEvent(new CustomEvent('previewFilesUpdated', {
                    detail: { projectId: vfsProjectId, serviceName: resolvedServiceName, compilationReady: true }
                }));
            }, 2000);
        }

        // BUG FIX (task #221) — live-confirmed: the service-selector dropdown
        // stayed stuck showing only the original service after the agent
        // called vfs_switch_service to start a genuinely new one (e.g.
        // "backend") — Canvas.jsx's own dropdown-refresh logic (its
        // handlePreviewUpdated, "new service appeared mid-session") only
        // ever runs off this SAME previewFilesUpdated event, which above is
        // only ever dispatched on a real file write/patch/scaffold — never
        // on vfs_switch_service alone. A newly-switched-to service has no
        // files yet at the moment of the switch itself, so the dropdown
        // stayed empty of it until the first successful write landed (a real
        // wait, easily tens of seconds into a multi-file scaffold) instead
        // of showing it immediately, the same instant the agent starts
        // addressing it. Dispatch the same event here too, but with
        // `compilationReady: false` — Canvas.jsx's dropdown-merge branch
        // doesn't gate on that flag at all, while every branch that would
        // attempt an actual preview render (there are no real files to
        // render yet) explicitly requires `compilationReady: true` and so
        // stays inert here.
        if (toolCall.name === 'vfs_switch_service' && isVfsToolCallSuccess(toolCall.response) && vfsProjectId) {
            const switchedServiceName = result?.service_path || toolCall.args?.name;
            if (switchedServiceName) {
                window.dispatchEvent(new CustomEvent('previewFilesUpdated', {
                    detail: { projectId: vfsProjectId, serviceName: switchedServiceName, compilationReady: false }
                }));
            }
        }

        if (
            // Task #210 — live-confirmed gap: the block below only registers
            // the service (`registerVfsService`) when the tool call's
            // project_id DIVERGES from the current `vfsProjectId` (a brand-new
            // or switched-to project). The far more common case — the chat is
            // already correctly bound to the CURRENT project, and this call
            // just confirms/creates its service (e.g. `vfs_ensure_project`
            // returning `created: true` for a project the user already had
            // open in Code) — never touched `registerVfsService` at all. The
            // service dropdown (Canvas.jsx, sourced from
            // `vfsClient.getVfsServiceNames`) stayed empty forever, so
            // clicking Code showed "Select service to preview..." with
            // nothing pre-selected and the preview never rendered — live-
            // confirmed: a real "Table Reservation App" turn hit exactly
            // this. Register the service here too, independent of the
            // project-divergence branch below, so the NEXT time Canvas.jsx
            // reads this project's service list (its own existing "auto-
            // select first service" logic, same pattern as task #77) it
            // actually finds something to select.
            (toolCall.name === 'vfs_create_project' || toolCall.name === 'vfs_ensure_project') &&
            result?.success &&
            result?.project_id &&
            result.project_id === vfsProjectId &&
            (result.service_path || result.service)
        ) {
            const confirmedServicePath =
                result.service_path ||
                (result.service ? `services/${result.service}` : null);
            if (confirmedServicePath) {
                registerVfsService(vfsProjectId, confirmedServicePath);
            }
        }

        if (
            // Task #201 added `vfs_ensure_project` as the agent's now-mandated
            // first-call tool, replacing ad hoc `vfs_create_project` calls for
            // the common case — but this mid-turn project-bound detection
            // (task #164) originally only recognized `vfs_create_project` by
            // name. Live-confirmed regression: a fresh chat's first turn now
            // calls `vfs_ensure_project`, this block never fired, and clicking
            // Code mid-turn still spawned a second, unrelated project exactly
            // like the bug #164 was built to fix. `vfs_ensure_project`'s
            // response has the same `project_id`/`success` shape either way
            // (`created: true` for a brand-new project, `false` when it found
            // and reused an existing one) — bind on it regardless of `created`,
            // since a project-id divergence from the FE's current `vfsProjectId`
            // needs the same sync either way.
            (toolCall.name === 'vfs_create_project' || toolCall.name === 'vfs_ensure_project') &&
            result?.success &&
            result?.project_id &&
            result.project_id !== vfsProjectId &&
            tenantId &&
            !projectSwitchedRef.current.has(toolCall.id)
        ) {
            if (hasEstablishedActivityRef.current) {
                // eslint-disable-next-line no-console
                console.warn(
                    '⚠️ vfs_create_project returned a different project id mid-conversation, but this session already has established activity in the current project — not auto-redirecting (task #142). New project id from the tool call:',
                    result.project_id
                );
            } else {
                projectSwitchedRef.current.add(toolCall.id);
                // Live-confirmed gap: when the AGENT (not the FE) creates the
                // project — e.g. it ran `vfs_bash("ls -la")` on an empty-but-
                // already-existing project, misjudged that as "no project
                // exists yet", and called vfs_create_project itself — this
                // redirect lands on a brand-new project id that never went
                // through quickProjectCreation.js's registerVfsService default.
                // The next turn's VfsChatPanel remount (new `key`, see
                // App.jsx) then has no default service to fall back to, so
                // its x-mia-vfs-service header is empty and every subsequent
                // plain-path write lands at the project ROOT instead of
                // services/app/ (live-confirmed: index.html/app.js next to
                // .mia/, no services/ folder at all). Register the same
                // default here so the remount has the same guarantee a
                // Home-created project already gets. `vfs_ensure_project`
                // reports the REAL service path it resolved or created
                // (`service_path`, e.g. "services/app" for a fresh project, or
                // whatever existing service it found reused) — use that when
                // present instead of assuming "services/app", since an
                // ensure-call against an already-populated project can
                // legitimately resolve to a different service.
                const boundServicePath = result.service_path || 'services/app';
                registerVfsService(result.project_id, boundServicePath);
                // Same-turn fallback for buildHeaders — see
                // pendingServiceNameRef's own doc above for why the prop
                // alone isn't enough here.
                pendingServiceNameRef.current = boundServicePath;
                pendingProjectIdRef.current = result.project_id;
                // Task #164 fix: tell the parent (App.jsx) the real project
                // id RIGHT NOW, synchronously — not gated on the turn
                // completing like the navigate() below. Without this, a user
                // who clicks the Chat<->Code switcher while this turn is
                // still streaming sees no bound project (canvasProjectContext
                // is never populated on the plain /chat door) and creates a
                // SECOND, unrelated project instead of opening this one.
                onProjectBound?.(result.project_id);
                // Don't redirect immediately — this used to call
                // onProjectRedirect?.()+navigate() synchronously right here,
                // which killed the rest of THIS turn's tool calls (e.g. the
                // vfs_write_file calls that actually write the project's
                // code) by remounting the widget and aborting the in-flight
                // run_sse stream. Instead, just record the redirect and let
                // handleTurnComplete (wired to the widget's real
                // onTurnComplete event, below) perform it once the turn has
                // genuinely finished.
                // Live-confirmed data-loss bug: this redirect changes
                // `vfsProjectId`, which is VfsChatPanel's `key` in App.jsx —
                // so the redirect remounts this whole component. Without a
                // `conversationId` in the URL, the remount's own
                // `initialSessionId` falls back to `vfs-${vfsProjectId}`
                // (line ~1002 below), which is now the NEW project id — a
                // session id the ADK-widget has never used for this turn.
                // The remounted widget starts a genuinely fresh session,
                // silently orphaning the transcript the user was just
                // looking at (the playbook intro card is all that's left).
                // Carry the session id THIS turn was actually run under —
                // computed from the pre-redirect `vfsProjectId`, same as the
                // widget's own `initialSessionId` prop below — through as an
                // explicit `conversationId` query param, so the remounted
                // widget resumes the same session instead of starting a new
                // one bound to the new project id.
                const currentSessionId = derivedSessionId;
                const redirectParams = new URLSearchParams(location.search || '');
                // Guard against the (should-be-impossible-here, but not worth
                // risking) case derivedSessionId is still undefined — setting
                // a URLSearchParams value to undefined stringifies it to the
                // literal text "undefined", which would be worse than just
                // not setting the param at all.
                if (currentSessionId) {
                    redirectParams.set('conversationId', currentSessionId);
                }
                pendingRedirectRef.current = {
                    tenantId,
                    projectId: result.project_id,
                    search: `?${redirectParams.toString()}`,
                };
            }
        }
    }, [vfsServiceName, onShowExternalPreview, vfsProjectId, tenantId, location.search, onProjectBound, derivedSessionId, triggerPostTurnResync, hideCanvas]);

    // Live-confirmed real bug: reopening a PAST conversation via its saved
    // `/chat?conversationId=...` URL (a plain page load, no live turn)
    // never enables the Code button, even though `.mia/project.json` on
    // disk clearly shows this exact conversationId created and owns a real
    // project. Root cause: `onProjectBound` above is only ever called from
    // `handleToolCall`, which only fires for tool calls in a LIVE turn —
    // the vendored ChatWidget's own history replay on mount
    // (`hydrateSession` in its `useChat` hook) loads past messages but
    // never re-emits their tool-call responses through `onToolCall`, so a
    // resumed conversation's binding never reaches this component at all
    // unless the user sends a brand-new message. Ask vfs-service directly
    // (reverse lookup: which project, if any, was created by this
    // conversationId) once per session id, same "best-effort, never block
    // the chat" spirit as the rest of this component's project-binding
    // logic.
    const conversationLookupRef = useRef(null);
    useEffect(() => {
        if (!hideCanvas || vfsProjectId || !requestedSessionId) { return; }
        if (conversationLookupRef.current === requestedSessionId) { return; }
        conversationLookupRef.current = requestedSessionId;
        let cancelled = false;
        findProjectByConversationId(requestedSessionId).then((projectId) => {
            if (cancelled || !projectId) { return; }
            pendingProjectIdRef.current = projectId;
            onProjectBound?.(projectId);
        });
        return () => { cancelled = true; };
    }, [hideCanvas, vfsProjectId, requestedSessionId, onProjectBound]);

    // Task #157 — live-confirmed real bug, found testing a plain service
    // switch (no project creation involved): `onProjectRedirect` (despite
    // its name) is really "the current pendingChatMessage has been
    // consumed, App.jsx may clear it" — it was only ever called from the
    // vfs_create_project redirect path above, so pendingChatMessage stayed
    // set to the FIRST turn's prompt for the rest of the conversation. That
    // was invisible as long as nothing else remounted this component in the
    // same project — but the moment something legitimately does (e.g.
    // switching to a different SERVICE within the same project, which
    // App.jsx's key now also covers, in the same task's own key fix), the
    // remount reads `initialMessage={pendingChatMessage}` synchronously and
    // RESENDS that stale first prompt into the new session. Confirmed live:
    // switching from a working Wordle service to a brand-new empty Chess
    // service and typing "create a simple chess game" silently resent
    // "create a wordle game" instead — the ADK session even got labeled
    // "Create Wordle Game" and `vfs_list_tree` on it showed the wordle
    // service's files. Call it on EVERY turn completion now, not only when
    // a redirect is pending, so pendingChatMessage never survives past the
    // turn it was actually sent for.
    // The AI service out of quota (adk-be-app's `rate_limited` event, or the raw 429 of an older backend): our own
    // notice with a countdown instead of the widget's exception text — see RateLimitNotice and utils/llmRateLimit.js.
    // One automatic retry per message: `autoRetriedRef` is set once a retry was sent, and cleared by a turn that
    // ends well or by the person typing (a new message is coming, so the old one is not replayed under it).
    const [rateLimitNotice, setRateLimitNotice] = useState(null);
    const autoRetriedRef = useRef(false);
    const turnFailedRef = useRef(false);
    const handleTurnError = useCallback((info) => {
        const rateLimit = rateLimitFromTurnError(info);
        if (!rateLimit) { return undefined; }
        turnFailedRef.current = true;
        setRateLimitNotice({ ...rateLimit, autoRetry: !autoRetriedRef.current, id: Date.now(), retry: info.retry });
        return true;
    }, []);
    const retryRateLimitedTurn = useCallback(() => {
        const retry = rateLimitNotice?.retry;
        autoRetriedRef.current = true;
        setRateLimitNotice(null);
        retry?.();
    }, [rateLimitNotice]);
    const dismissRateLimitNotice = useCallback(() => setRateLimitNotice(null), []);
    const pauseRateLimitAutoRetry = useCallback((event) => {
        const tag = event.target?.tagName;
        if (tag !== 'TEXTAREA' && tag !== 'INPUT') { return; }
        autoRetriedRef.current = false;
        setRateLimitNotice((notice) => (notice?.autoRetry ? { ...notice, autoRetry: false } : notice));
    }, []);

    const handleTurnComplete = useCallback(() => {
        // A turn that ended well takes the rate-limit notice with it.
        if (!turnFailedRef.current) {
            autoRetriedRef.current = false;
            setRateLimitNotice(null);
        }
        turnFailedRef.current = false;
        turnIndexRef.current += 1;
        turnCompleted(vfsProjectId || pendingProjectIdRef.current, { changed: turnChangedRef.current });
        turnChangedRef.current = false;
        // Lets the parent know the agent has finished (it starts the "Open the project" countdown only then).
        onTurnComplete?.();
        // BUG FIX (live-reported, repeated across multiple fresh test
        // projects even after the previewResyncedRef/servicesToSync fixes —
        // "the same errors, nothing changed"): the resync chain
        // (triggerPostTurnResync, see its own doc above) used to run ONLY
        // from inside the vfs_get_preview_url tool-call handler, so a turn
        // that never called that exact tool — or whose response didn't
        // exactly match `{success: true, preview_url}` — silently never
        // resynced at all, no matter how many files the agent actually
        // wrote. handleTurnComplete is the one signal this file already
        // trusts as unconditional (the vendored ChatWidget's real
        // onTurnComplete event, wired below) — call the exact same resync
        // here too as a guaranteed fallback, independent of which tools
        // the agent happened to call this turn.
        const turnResyncProjectId = vfsProjectId || pendingProjectIdRef.current;
        if (turnResyncProjectId) {
            triggerPostTurnResync(turnResyncProjectId, vfsServiceName || '.');
        }
        onProjectRedirect?.();
        const pending = pendingRedirectRef.current;
        pendingRedirectRef.current = null;
        if (!pending) { return; }
        // Live-confirmed regression: on the plain /chat door this
        // unconditionally navigated to /code the instant the agent's first
        // vfs_create_project/vfs_ensure_project response landed — a few
        // seconds into any fresh Home-started conversation. From the
        // user's perspective the running chat just vanished, replaced by
        // an apparently brand-new one (VfsChatPanel remounts with a wholly
        // different key/props on /code). This auto-jump predates
        // ChatColumn/ChatPreviewPanel (this session's embedded Chat-mode
        // preview + file browser) — there is no reason left to leave /chat
        // at all here; chatBoundProjectId/onProjectBound already give the
        // parent everything it needs (the embedded panel, the Code
        // button's enabled state) without a navigation. Only follow the
        // agent's project switch with a real URL change in Code mode,
        // where the URL is supposed to track the active project.
        if (hideCanvas) { return; }
        navigate(`/code/${pending.tenantId}/${pending.projectId}${pending.search || ''}`, { replace: true });
    }, [navigate, onProjectRedirect, onTurnComplete, hideCanvas, vfsProjectId, vfsServiceName, triggerPostTurnResync]);

    // Fresh project (a real navigation to a different vfsProjectId, not just
    // a re-render) — reset the established-activity tracking so a NEW
    // project's first vfs_create_project divergence (the real #89 scenario)
    // is still allowed to auto-redirect.
    useEffect(() => {
        hasEstablishedActivityRef.current = false;
    }, [vfsProjectId]);
    const handleNewChat = useCallback(() => {
        toolCallIndexRef.current = new Map();
        turnIndexRef.current = 0;
        turnChangedRef.current = false;
        setRateLimitNotice(null);
        autoRetriedRef.current = false;
        setToolCalls([]);
        setIsTimelineOpen(false);
        pendingRedirectRef.current = null;
        // The widget's "New chat" button, `/new` and `/clear` all land here, after the widget has already abandoned
        // its session and started a fresh one (its own built-ins; host slash commands cannot override them).
        //
        // Inside a project (owner decision, 2026-10-31): a fresh conversation in the SAME project, never a new
        // project. The project stays bound — `vfsProjectId`, or on the plain /chat door the one this chat bound
        // (pendingProjectIdRef, still sent as `x-mia-vfs-project`, so adk-be-app scopes the new session to it exactly
        // as it does for any project's chat) — and the host moves the URL's `conversationId` to the new conversation.
        // The previous one stays in the project's history (/conversations, /resume). A new project comes from
        // Projects › New Project or `/project new`.
        //
        // Outside a project: `onNewConversation` (App.jsx's handleResetConversation), as before.
        const boundProjectId = vfsProjectId || pendingProjectIdRef.current;
        if (boundProjectId) {
            pendingProjectIdRef.current = boundProjectId;
            awaitingFreshSessionRef.current = true;
            onNewChatInProject?.(boundProjectId);
            return;
        }
        onNewConversation?.();
    }, [onNewConversation, onNewChatInProject, vfsProjectId]);
    const toggleTimeline = useCallback(() => setIsTimelineOpen((open) => !open), []);

    const resolveByIndexOrMatch = useCallback((arg, list, matches) => {
        const trimmed = arg.trim();
        if (/^\d+$/.test(trimmed)) {
            return list[Number(trimmed) - 1];
        }
        return list.find((item) => matches(item, trimmed));
    }, []);

    const slashCommands = useMemo(() => [
        {
            name: 'project',
            description: 'list or select a project',
            run: async (arg) => {
                const trimmed = arg.trim();
                const [newKeyword, ...rest] = trimmed.split(/\s+/);
                if (trimmed && newKeyword === 'new') {
                    const name = rest.join(' ').trim();
                    setCommandResult({
                        kind: 'info',
                        title: '/project new',
                        message: name
                            ? `Opening the project switcher to create "${name}" — this app always chats inside an existing project, so creating one goes through the full template wizard rather than this command.`
                            : 'Usage: /project new <name>',
                    });
                    if (name) { navigate('/projects'); }
                    return;
                }
                setCommandResult({ kind: 'list', title: 'Projects', loading: true, items: [] });
                try {
                    const { data } = await miaPlatformDataService.getProjects();
                    const projects = (data || []).map((p) => miaPlatformDataService.formatProject(p));
                    if (trimmed) {
                        const target = resolveByIndexOrMatch(trimmed, projects, (p, q) => p.id === q || p.name.toLowerCase() === q.toLowerCase());
                        if (target) {
                            navigate(`/code/${target.tenantId || tenantId}/${target.id}`);
                            setCommandResult(null);
                            return;
                        }
                        setCommandResult({ kind: 'list', title: 'Projects', error: `No project matches "${trimmed}".`, items: [] });
                        return;
                    }
                    setCommandResult({
                        kind: 'list',
                        title: 'Projects — pick one to switch',
                        items: projects.map((p) => ({
                            key: p.id,
                            label: p.name,
                            sublabel: p.id === vfsProjectId ? 'current' : undefined,
                            onSelect: () => {
                                navigate(`/code/${p.tenantId || tenantId}/${p.id}`);
                                closeCommandResult();
                            },
                        })),
                    });
                } catch (err) {
                    setCommandResult({ kind: 'list', title: 'Projects', error: err.message, items: [] });
                }
            },
        },
        {
            name: 'service',
            description: 'list or select a service in the current project',
            run: async (arg) => {
                const trimmed = arg.trim();
                if (trimmed === 'none') {
                    navigate(`/code/${tenantId}/${vfsProjectId}`);
                    return;
                }
                setCommandResult({ kind: 'list', title: 'Services', loading: true, items: [] });
                try {
                    const configuration = await miaPlatformDataService.getMicroservices(vfsProjectId, DEFAULT_REVISION);
                    const services = Object.entries(configuration?.services || {})
                        .map(([name, svc]) => miaPlatformDataService.formatMicroservice(name, svc));
                    if (trimmed) {
                        const target = resolveByIndexOrMatch(trimmed, services, (s, q) => s.name.toLowerCase() === q.toLowerCase());
                        if (target) {
                            navigate(`/code/${tenantId}/${vfsProjectId}/${encodeURIComponent(target.name)}/${DEFAULT_REVISION}/code`);
                            setCommandResult(null);
                            return;
                        }
                        setCommandResult({ kind: 'list', title: 'Services', error: `No service matches "${trimmed}".`, items: [] });
                        return;
                    }
                    setCommandResult({
                        kind: 'list',
                        title: 'Services — pick one to switch',
                        items: services.map((s) => ({
                            key: s.name,
                            label: s.name,
                            sublabel: s.name === vfsServiceName ? 'current' : undefined,
                            onSelect: () => {
                                navigate(`/code/${tenantId}/${vfsProjectId}/${encodeURIComponent(s.name)}/${DEFAULT_REVISION}/code`);
                                closeCommandResult();
                            },
                        })),
                    });
                } catch (err) {
                    setCommandResult({ kind: 'list', title: 'Services', error: err.message, items: [] });
                }
            },
        },
        {
            name: 'run',
            description: 'start a background agent workflow run',
            run: async (arg) => {
                const workflow = arg.trim();
                if (!workflow) {
                    setCommandResult({ kind: 'info', title: '/run', message: 'Usage: /run <workflow>' });
                    return;
                }
                setCommandResult({ kind: 'info', title: '/run', loading: true, message: '' });
                try {
                    const result = await startRun(workflow);
                    setCommandResult({
                        kind: 'info',
                        title: '/run',
                        message: `Started "${workflow}" — run ${result?.runId || result?.id || '(unknown id)'}.`,
                    });
                } catch (err) {
                    setCommandResult({ kind: 'info', title: '/run', error: err.message, message: '' });
                }
            },
        },
        {
            name: 'runs',
            description: 'list recent workflow runs',
            run: async () => {
                setCommandResult({ kind: 'list', title: 'Recent runs', loading: true, items: [] });
                try {
                    const runs = await listRuns();
                    setCommandResult({
                        kind: 'list',
                        title: 'Recent runs',
                        items: runs.map((r) => ({
                            key: r.runId,
                            label: `${r.workflow} — ${r.status}`,
                            sublabel: r.runId,
                        })),
                    });
                } catch (err) {
                    setCommandResult({ kind: 'list', title: 'Recent runs', error: err.message, items: [] });
                }
            },
        },
        {
            name: 'cancel',
            description: 'cancel a run (defaults to the most recent active one)',
            run: async (arg) => {
                const explicitId = arg.trim();
                setCommandResult({ kind: 'info', title: '/cancel', loading: true, message: '' });
                try {
                    let runId = explicitId;
                    if (!runId) {
                        const runs = await listRuns();
                        const active = runs.find((r) => ['running', 'pending', 'queued'].includes((r.status || '').toLowerCase()));
                        if (!active) {
                            setCommandResult({ kind: 'info', title: '/cancel', message: 'No active run to cancel.' });
                            return;
                        }
                        runId = active.runId;
                    }
                    await cancelRun(runId);
                    setCommandResult({ kind: 'info', title: '/cancel', message: `Cancelled run ${runId}.` });
                } catch (err) {
                    setCommandResult({ kind: 'info', title: '/cancel', error: err.message, message: '' });
                }
            },
        },
        {
            name: 'files',
            description: "browse the current project's files",
            run: async (arg) => {
                const path = arg.trim() || '.';
                setCommandResult({ kind: 'list', title: `/files ${path}`, loading: true, items: [] });
                try {
                    const { isDirectory, entries } = await listDirectoryEntries(vfsProjectId, path);
                    if (isDirectory) {
                        setCommandResult({
                            kind: 'list',
                            title: `${path === '.' ? '/' : path}`,
                            items: entries
                                .sort((a, b) => (a.type === b.type ? a.name.localeCompare(b.name) : (a.type === 'directory' ? -1 : 1)))
                                .map((entry) => ({
                                    key: entry.name,
                                    label: entry.type === 'directory' ? `${entry.name}/` : entry.name,
                                })),
                        });
                        return;
                    }
                    // Not a directory (or vfs-service reports it as a file
                    // node) — fetch its content instead.
                    const content = await readFile(vfsProjectId, path);
                    setCommandResult({ kind: 'text', title: path, text: content });
                } catch (err) {
                    setCommandResult({ kind: 'list', title: `/files ${path}`, error: err.message, items: [] });
                }
            },
        },
    ], [vfsProjectId, vfsServiceName, tenantId, navigate, resolveByIndexOrMatch, closeCommandResult]);

    if (!userId) {
        // Mirror the loading-state convention used elsewhere in App.jsx
        // (e.g. the project grid's `isLoadingProjects` state) while auth
        // resolves.
        return (
            <div className="chat-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                    <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                </div>
            </div>
        );
    }

    // This app has no `data-theme` attribute the widget could auto-detect
    // (see its README: falls back to `prefers-color-scheme` when absent) —
    // it defaults to dark itself (see index.jsx's `localStorage.getItem
    // ('theme') || 'dark'`) via its own hand-rolled dark CSS, not a
    // documented DOM signal. Left at antd's default light algorithm, the
    // widget renders as a washed-out light panel inside this app's near-
    // black chrome — verified live. Match it explicitly instead.
    // isDark/chatWidgetConfig are computed earlier (alongside buildHeaders) —
    // every hook must run before this component's early `!userId` return
    // above, so they can't be declared down here next to where they're used.

    return (
        <div className="chat-container" onKeyDownCapture={pauseRateLimitAutoRetry} ref={chatContainerRef} style={{ position: 'relative' }}>
            {/* BUG FIX (live-reported, screen-recorded: "empty chat, then the
                prompt appears after a while"): isClassifyingInitialMessage
                withholds `initialMessage` from the widget below on purpose
                (see its own doc comment — a real, live-confirmed race
                otherwise aborts the first run_sse) while the Auto playbook
                classification call is in flight, and the widget's own empty
                start screen (playbook intro, "Try with" cards) is what fills
                that gap — reading as "did my message even go through?". This
                overlay shows the user's own text immediately, honestly
                labeled as sending rather than pretending it's already in the
                transcript, without touching the race-safe gating itself. */}
            {isClassifyingInitialMessage && (
                // BUG FIX (live-reported: hexagons visible but never move no
                // matter how the drift duration was tuned): App.jsx wraps
                // every non-home/login route (including /chat, where this
                // overlay lives) in a `.disable-animations` class that kills
                // ALL animations/transitions app-wide with `!important`
                // (App.css) — a performance guard, with exactly one
                // documented exemption: `[role="status"]` elements (already
                // used by the vendored chat widget's own spinners, per that
                // CSS rule's own comment). Duration/speed were never the
                // real problem; the animation was being nuked outright.
                // role="status" is also the correct semantic here anyway
                // (a live loading indicator).
                <div className="vfs-chat-sending-overlay" role="status">
                    {/* Own hexagon layout (see makeSendingHexagons' own
                        comment) — same shape/color family as Home.jsx's
                        ambient background, generated independently so
                        visibility doesn't depend on winning a CSS cascade
                        fight against Home's own rules. */}
                    <div className="vfs-chat-sending-hexagons">
                        {sendingHexagons.map((hex) => (
                            <div
                                className={`vfs-chat-sending-hex vfs-chat-sending-hex-${hex.driftIndex}`}
                                key={hex.id}
                                style={{
                                    top: hex.top,
                                    left: hex.left,
                                    width: `${hex.size}px`,
                                    height: `${hex.size}px`,
                                    color: hex.color,
                                    animationDuration: hex.duration,
                                    animationDelay: hex.delay,
                                }}
                            >
                                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                                    <polygon points="50 0, 93.301 25, 93.301 75, 50 100, 6.699 75, 6.699 25" />
                                </svg>
                            </div>
                        ))}
                    </div>
                    {/* User's own message is intentionally NOT repeated here
                        (a blue chat-bubble echo was live-reported as
                        unwanted clutter) — the point of this overlay is
                        just "something is happening", not a preview of the
                        transcript; the real message shows up once the
                        widget actually mounts it. Same word list/cadence
                        as the widget's own
                        WaitingIndicator (see SENDING_WAITING_WORDS' own
                        comment) — shown large, since this overlay has the
                        whole panel to itself instead of sharing one line
                        inside a chat bubble. */}
                    <div className="vfs-chat-sending-word">
                        {SENDING_WAITING_WORDS[sendingWordIndex]}
                    </div>
                </div>
            )}
            {/* cssVar: true — without it, antd only exposes design tokens via the
                useToken() hook / scoped generated classes, never as global
                --ant-* custom properties. The widget's own CSS Modules
                (e.g. ChatMessage.module.css's .bubbleUser) read --ant-color-primary
                directly with no fallback; undefined, its `background` becomes
                invalid (transparent) while the paired white text still applies —
                a user's own message renders white-on-white. Confirmed by reading
                mia-chatbot-widget/src/styles/tokens.css's own comment: "--ant-*
                tokens are provided by the antd ConfigProvider (peer dependency)" —
                that dependency was never actually satisfied here. */}
            {/* A turn the plan refuses (403 `plan_feature`) gets no explanation from the widget itself. */}
            <PlanRefusalNotice />
            <CreditsNotice me={credits.me} notice={credits.notice} onRecheck={credits.refresh} />
            {onOwnKey && ownKeyState === 'missing' && <OwnKeyPrompt />}
            {/* Mounted only once the plan says whether AI Foundry is in use (right away without sandbox-first), so the
                widget never asks for the organization's models for a person who has them off. */}
            {(!planFeatures.ready || (onOwnKey && ownKeyState === 'checking')) && (
                <div className="vfs-chat-key-checking" role="status">{planFeatures.ready ? 'Checking your model key…' : 'Loading…'}</div>
            )}
            {planFeatures.ready && !(onOwnKey && ownKeyState !== 'saved') && (
            // Credits used up and enforced: the chat waits (`inert`: no typing, no clicks; the wrapper has no box of its own).
            <div className="vfs-chat-slot" inert={creditsHold}>
            <ConfigProvider theme={{ algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm, cssVar: true }}>
                <ChatWidgetRootTokenSync />
                <ChatWidget
                    // Live-confirmed regression: the widget's own props API
                    // moved `title`/`leadingAction` (and 7 other flat props,
                    // none of which this file ever used) under a single
                    // `header` object — `title`/`leadingAction` as flat
                    // top-level props on <ChatWidget> are simply ignored by
                    // the current widget source (no backward-compat shim),
                    // silently dropping the Chat/Code switcher and the
                    // "Auto" playbook picker entirely. This was already true
                    // of the widget's SOURCE since an earlier merge, but the
                    // vendored build in use for most of this session
                    // predated that merge (never rebuilt since); only once
                    // the package was rebuilt (for the file-edit-preview
                    // feature) did the dist/ output finally reflect it.
                    header={{
                        // task #216: "Assistant" was a static, redundant
                        // label — the Playbook dropdown right next to it
                        // already says what this chat is. Empty string (not
                        // omitted) deliberately overrides the widget's own
                        // "Assistant" fallback title.
                        title: '',
                        // Task #135: moved from `headerActions` (right-
                        // aligned slot, grouped with Playbook/History/
                        // Download/New-chat — crowded that row per user
                        // feedback on #133) to `leading`, rendered in the
                        // widget's `.headerLeading` div immediately before
                        // the title. Built by MainChat (App.jsx), which
                        // already owns the project/tenant context and
                        // navigate() calls needed to switch modes (see task
                        // #131/#133 history).
                        leading: (
                            <>
                                {headerActions}
                                <PlaybookPicker
                                    disabled={aiFoundryOff || !planFeatures.playbooks}
                                    disabledReason={aiFoundryOff
                                        ? `${NEEDS_AI_FOUNDRY} to choose an assistant. Flow’s default one answers.`
                                        : `Choosing an assistant isn’t included in ${planLabel(planFeatures.planName)}. The default one answers.`}
                                    playbooks={aiFoundryOff ? [DEFAULT_PLAYBOOK_OPTION] : playbooks}
                                    resolvedName={aiFoundryOff ? DEFAULT_PLAYBOOK_NAME : resolvedPlaybookName}
                                    selected={aiFoundryOff ? DEFAULT_PLAYBOOK_NAME : playbookChoice}
                                    onSelect={setPlaybookChoice}
                                />
                            </>
                        ),
                        // Real header content, not absolutely-positioned overlays —
                        // see WorkflowTimelineToggle's own doc for why these two
                        // moved here from a `position: absolute; top: 76px` hack
                        // that assumed a fixed header height and could float on
                        // top of live chat content the moment that assumption
                        // drifted even slightly.
                        actions: (
                            <>
                                <WorkflowTimelineToggle
                                    isOpen={isTimelineOpen}
                                    onToggle={toggleTimeline}
                                    toolCalls={toolCalls}
                                />
                            </>
                        ),
                    }}
                    // Flow already has its own full History page (left sidebar) that
                    // shows every conversation across every playbook — the widget's
                    // own in-chat history button/modal only ever showed THIS single
                    // playbook's sessions (or, in an earlier attempt to fix that,
                    // would have needed its own separate multi-playbook plumbing).
                    // Two differently-scoped history entry points was confusing
                    // (same list, different content) — keep one, on the left.
                    hideHistoryButton
                    // Only `prompts` is set — `highlights`/`subtitle`/`content`
                    // stay omitted so the widget keeps its own defaults
                    // (the playbook's skills / description) for those.
                    overview={{ prompts: chatSuggestions }}
                    // Lets the widget resolve appName/agentName/slashPrompts itself
                    // from the playbook (GET /api/playbooks), instead of us
                    // hardcoding the playbook-scoped composite runtime name
                    // ("code_generation_playbook_code_generation_agent") by hand —
                    // see ChatWidgetProps.fixedPlaybookName's own doc: "the widget
                    // loads this playbook by name internally and configures itself
                    // automatically". Confirmed live this derives the exact same
                    // scoped name a hand-written one would (adk-be-app's
                    // DatabaseAgentLoader only ever loads playbook-scoped runtime
                    // identities into the runner — the bare agent name
                    // "code-generation-agent" 404s /run_sse the same way a
                    // hand-picked wrong name would).
                    //
                    // One known gap this doesn't paper over: the widget derives its
                    // own `agentName` (used only for the separate GET
                    // /api/agents/{agentName} catalog/metadata lookup) as
                    // snake_case ("code_generation_agent"), but this adk-be-app
                    // instance registers agents with dashes ("code-generation-
                    // agent") — a real naming-convention mismatch between the
                    // widget and this backend, not something fixable from here.
                    // That lookup 404s either way (confirmed); it's non-fatal —
                    // the widget degrades gracefully and the chat itself still
                    // works, verified live end-to-end.
                    // Was hardcoded to "code-generation-playbook" — now
                    // whatever the picker above resolved: an explicit user
                    // choice, or Auto's best-match pick (see
                    // resolvedPlaybookName's own effects above). Falls back
                    // to the original default if nothing has resolved yet
                    // (e.g. playbooks still loading) rather than mounting
                    // with an empty name.
                    //
                    // REVERTED (live-confirmed real regression): briefly
                    // tried leaving this `undefined` while
                    // isClassifyingInitialMessage was true, to avoid the
                    // widget firing its own agent-metadata lookup for the
                    // wrong default playbook. That assumed the widget
                    // gracefully waits with no `fixedPlaybookName` — it does
                    // not: it computed an EMPTY app name and still attempted
                    // POST /run_sse with it (confirmed live:
                    // "apps//users/.../sessions/..." — a literal empty
                    // segment — then a real 400 on run_sse). Always passing
                    // a real, non-empty name (even if it's about to be
                    // replaced the instant classification lands) is the
                    // only state this widget version actually handles
                    // safely.
                    fixedPlaybookName={widgetPlaybookName}
                    // Deterministic — the SAME id every time this exact
                    // project/service pair is opened, not a random one per
                    // mount — so closing and reopening the tab (or just
                    // switching away and back) resumes the same conversation,
                    // reconnecting to whatever adk-be-app is still doing in
                    // the background, instead of starting a new, empty one
                    // that looks stopped even though real work is in flight.
                    // Live now: mia-chatbot-widget's initialSessionId prop
                    // (feature/initial-session-id, commits 991c94b/9891d83)
                    // built and vendored at vendor/mia-chatbot-widget-0.2.0-1.tgz
                    // — that branch isn't merged to main/released to the real
                    // registry yet, so this is a stopgap pending that; bump
                    // back to a registry version once it lands there.
                    // Task #211 — explicit product correction, live-confirmed
                    // as a real bug: the session id used to also embed
                    // `vfsServiceName` (one distinct ADK session PER SERVICE
                    // within a project, from task #157). That's now wrong on
                    // purpose — "the session is related to the project not to
                    // a single service" — and it was also the root cause of a
                    // real concurrency bug: App.jsx's chat-widget `key` prop
                    // includes the selected service name, so switching the
                    // Preview panel's service dropdown remounted this whole
                    // component mid-turn. A remount while the OLD session's
                    // detached execution (src.agent.detached_execution,
                    // adk-be-app) was still actively running triggered a NEW
                    // attach/session-create for what the FE now considered
                    // "current", racing the still-running original execution
                    // against the backend's session record — live-confirmed
                    // via adk-be-app logs: two `session_attach: client
                    // attaching to active run` events ~7s apart for the same
                    // session_id, immediately followed by
                    // `detached_execution: agent execution raised` and
                    // `AGENT_ERROR: The session has been modified in storage
                    // since it was loaded`, plus lost tool-call results and a
                    // failed turn. Session id is now project-scoped only —
                    // switching services no longer touches the chat session
                    // at all, closing off this entire remount-triggered race
                    // at the source (see App.jsx's chat-widget `key` prop,
                    // fixed alongside this to drop the service name too).
                    initialSessionId={derivedSessionId}
                    // Lets the host (Canvas's "Ask AI for Help" / "Fix with AI" buttons,
                    // via App.jsx's pendingChatMessage state) hand a message straight into
                    // this conversation instead of the user retyping the error by hand.
                    // Withheld (undefined) while Auto is still classifying it — see
                    // effectiveInitialMessage's own comment above for why.
                    initialMessage={effectiveInitialMessage}
                    pageContext={pageContext}
                    pendingMessage={widgetSettled ? pendingMessage : undefined}
                    onSessionChange={handleSessionChange}
                    config={chatWidgetConfig}
                    // Passing this (even were it `[]`) also opts this chat into
                    // the widget's own built-in `/conversations`, `/resume`,
                    // `/new`, `/clear` — see mia-chatbot-widget's
                    // ChatWidgetProps.slashCommands doc.
                    slashCommands={slashCommands}
                    onToolCall={handleToolCall}
                    onOpenFile={onOpenFile}
                    onTurnComplete={handleTurnComplete}
                    // Our own notice for a rate-limited turn; every other failure keeps the widget's.
                    onTurnError={handleTurnError}
                    onNewChat={handleNewChat}
                    // Fires the instant this conversation actually starts (a
                    // message is sent) — earlier than any tool call, and
                    // before a project is necessarily bound (that needs the
                    // agent's own vfs_create_project/vfs_ensure_project
                    // response). Lets the host (App.jsx's goToMode) tell "no
                    // activity yet, a brand-new vfs project on Code-
                    // switch is legitimate" apart from "activity has
                    // started, a REAL project is imminent — never silently
                    // substitute a new one."
                    onChatStarted={onActivityStart}
                />
            </ConfigProvider>
            </div>
            )}
            {rateLimitNotice && (
                <RateLimitNotice
                    autoRetry={rateLimitNotice.autoRetry}
                    details={rateLimitNotice.details}
                    key={rateLimitNotice.id}
                    onDismiss={dismissRateLimitNotice}
                    onRetry={retryRateLimitedTurn}
                    retryAfterSeconds={rateLimitNotice.retryAfterSeconds}
                />
            )}
            <WorkflowTimeline
                isOpen={isTimelineOpen}
                onToggle={toggleTimeline}
                toolCalls={toolCalls}
                vfsProjectId={vfsProjectId || pendingProjectIdRef.current}
            />
            <CommandResultModal onClose={closeCommandResult} result={commandResult} />
        </div>
    );
}

VfsChatPanel.propTypes = {
    vfsProjectId: PropTypes.string.isRequired,
    vfsServiceName: PropTypes.string,
    initialMessage: PropTypes.string,
    // Forwarded straight through to the vendored ChatWidget's own
    // `pageContext` prop — sent to adk-be-app as `state_delta.page_context`,
    // injected into the agent's system instructions, NEVER rendered as
    // message text. Used by the "Ask AI" (Preview Logs) flow to hand the
    // agent raw error/log text without dumping it into the visible chat
    // transcript (task #158). See PageContext's own shape requirements
    // (page/path/summary required, data optional) in the widget's types.
    pageContext: PropTypes.shape({
        page: PropTypes.string,
        path: PropTypes.string,
        summary: PropTypes.string,
        data: PropTypes.object,
    }),
    // Injects a message into the conversation NOW, even if it has already
    // started (unlike `initialMessage`, which is deliberately blocked once
    // the conversation is active — see the widget's own ChatWidget.tsx
    // comment). `id` must be a new value each time a send should happen.
    pendingMessage: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.string,
    }),
    // Written into the chat's input (not sent) for the person to finish: see utils/askAiInChat.js. New `id`, new draft.
    pendingDraft: PropTypes.shape({
        id: PropTypes.string,
        text: PropTypes.string,
    }),
    // Needed only for /project and /service's navigate() calls — falls back
    // to the current project's own tenantId (resolved above) when a target
    // project's tenantId isn't already known from the fetched list.
    tenantId: PropTypes.string,
    // "New chat", `/new` or `/clear` outside a project (see handleNewChat).
    onNewConversation: PropTypes.func,
    // (projectId) => void — "New chat", `/new` or `/clear` inside a project: the host keeps the project and moves
    // the URL to a fresh conversation (see handleNewChat).
    onNewChatInProject: PropTypes.func,
    // (sessionId) => void — the first session id of a conversation started by onNewChatInProject.
    onFreshConversationStarted: PropTypes.func,
    // task #81 — (serviceName, url) => void. Called once vfs_get_preview_url
    // returns a real, ready preview so the host (App.jsx) can jump Canvas to
    // its Preview tab. Optional: absent in contexts with no Canvas to show
    // (the plain /chat door), in which case this is simply never invoked.
    onShowExternalPreview: PropTypes.func,
    // Called synchronously, in the same tick as the vfs_create_project
    // redirect's navigate() — see that call site's own comment for why this
    // MUST be synchronous rather than an effect reacting to the URL change.
    onProjectRedirect: PropTypes.func,
    onProjectBound: PropTypes.func,
    // Called once, the instant this conversation actually starts (a
    // message is sent) — see the onChatStarted wiring above for why.
    onActivityStart: PropTypes.func,
    onTurnComplete: PropTypes.func,
    onOpenFile: PropTypes.func,
    // Called once vfs_get_preview_url's resulting preview is shown AND
    // IndexedDB has been force-reconciled from vfs-service for it — the
    // signal App.jsx uses to enable the Code button (see its own
    // chatCodeReady state). See the vfs_get_preview_url handler above.
    onCodeReady: PropTypes.func,
    // task #131 — extra content rendered in the vendored ChatWidget's own
    // header (its `headerActions` slot), e.g. MainChat's Chat/Code/Agent
    // mode selector. Optional, forwarded through verbatim.
    headerActions: PropTypes.node,
    // True when the host (App.jsx's ChatColumn) is already showing this same
    // preview inline in an embedded panel — suppresses the floating "Open
    // Preview" button so the two controls don't duplicate each other.
    previewEmbedded: PropTypes.bool,
    // True on the plain /chat door — see handleTurnComplete's own doc for
    // why this suppresses its auto-navigate-to-/code side effect there.
    hideCanvas: PropTypes.bool,
    // Same hand-off signal as `?playbookId=` in the URL, for a caller that
    // renders this panel directly with no route/query-string of its own to
    // carry a param through (Home.jsx's embedded chat view) — see
    // playbookIdHint's own comment above.
    initialPlaybookId: PropTypes.string,
    initialConversationId: PropTypes.string,
};
